import { GameAudio } from "./audio";
import { drawPixelCritter, drawPixelStunStars, type CritterSpriteState } from "./critter-sprites";
import { InputState } from "./input";
import {
  ALL_LEVELS,
  LEVELS,
  freshCollectibles,
  freshCritters,
  type Collectible,
  type GroveCritter,
  type Hazard,
  type LevelDefinition,
  type Platform,
  type PowerUpKind,
  type PowerGate,
  type SecretArea,
  type Slope,
  type Spring,
} from "./level";

export type GameState = "ready" | "map" | "playing" | "paused" | "won" | "worldcomplete" | "gameover";

export interface LevelProgress {
  name: string;
  worldNumber: number;
  worldName: string;
  worldLevel: number;
  bonus: boolean;
  unlocked: boolean;
  completed: boolean;
  bestPins: number;
  crown: boolean;
  bestTime: number | null;
  grade: "" | "Bronze" | "Silver" | "Gold";
}

export interface GameStats {
  score: number;
  pins: number;
  fruits: number;
  lives: number;
  time: number;
  bestTime: number | null;
  checkpoint: boolean;
  crown: boolean;
  levelIndex: number;
  levelName: string;
  levelSubtitle: string;
  levelObjective: string;
  worldNumber: number;
  worldName: string;
  worldLevel: number;
  worldLevelCount: number;
  totalLevels: number;
  parTime: number;
  bonus: boolean;
  canAdvance: boolean;
  crownCount: number;
  bonusUnlocked: boolean;
  goldenHero: boolean;
  levels: LevelProgress[];
}

interface GameCallbacks {
  onStateChange: (state: GameState, stats: GameStats) => void;
  onToast: (message: string) => void;
  onDebugChange?: (enabled: boolean) => void;
}

interface Player {
  x: number;
  y: number;
  w: number;
  h: number;
  vx: number;
  vy: number;
  facing: -1 | 1;
  onGround: boolean;
  coyote: number;
  jumpBuffer: number;
  power: PowerUpKind | null;
  powerTimer: number;
  extraJumpUsed: boolean;
  invincible: number;
  respawnTimer: number;
  landingTimer: number;
  runCycle: number;
  airTime: number;
  gliding: boolean;
  standingPlatform: string | null;
  powerFlash: number;
}

interface SpriteFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  gravity: number;
}

interface PopupText {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
  maxLife: number;
}

interface RuntimePlatform extends Platform {
  runtimeId: string;
  originX: number;
  originY: number;
  active: boolean;
  broken: boolean;
  dx: number;
  dy: number;
}

interface SavedLevel {
  completed: boolean;
  bestPins: number;
  crown: boolean;
  bestTime: number | null;
  grade: "" | "Bronze" | "Silver" | "Gold";
}

interface SavedProgress {
  version: 2;
  levels: SavedLevel[];
}

const FIXED_STEP = 1 / 120;
const GRAVITY = 2450;
const NORMAL_WIDTH = 50;
const NORMAL_HEIGHT = 74;
const MINI_WIDTH = 34;
const MINI_HEIGHT = 48;
const SPRITE_SCALE = 1.1;
const GROUNDED_SPRITE_HEIGHT = 96;
const CRITTER_HALF_WIDTH = 27;
const CRITTER_HEIGHT = 52;
const CRITTER_STUN_TIME = 4.5;
const CRITTER_DEFEAT_TIME = 0.58;
const PROGRESS_KEY = "uppg-progress-v2";

// These measured bounds isolate each pose in the generated 8x4 artwork and
// preserve the clean, foot-anchored animation introduced in version 0.1.1.
const SPRITE_FRAMES: readonly SpriteFrame[] = [
  { x: 33, y: 13, w: 80, h: 104 }, { x: 152, y: 13, w: 79, h: 104 },
  { x: 270, y: 13, w: 83, h: 104 }, { x: 389, y: 13, w: 86, h: 104 },
  { x: 514, y: 13, w: 71, h: 104 }, { x: 631, y: 13, w: 71, h: 104 },
  { x: 727, y: 50, w: 64, h: 70 }, { x: 805, y: 50, w: 64, h: 70 },
  { x: 22, y: 126, w: 82, h: 91 }, { x: 137, y: 129, w: 79, h: 89 },
  { x: 252, y: 129, w: 75, h: 88 }, { x: 365, y: 129, w: 78, h: 87 },
  { x: 477, y: 132, w: 75, h: 84 }, { x: 587, y: 131, w: 75, h: 87 },
  { x: 693, y: 133, w: 72, h: 85 }, { x: 797, y: 132, w: 70, h: 87 },
  { x: 31, y: 248, w: 73, h: 68 }, { x: 138, y: 226, w: 71, h: 88 },
  { x: 243, y: 225, w: 77, h: 86 }, { x: 356, y: 220, w: 78, h: 83 },
  { x: 472, y: 229, w: 78, h: 82 }, { x: 589, y: 233, w: 74, h: 81 },
  { x: 698, y: 236, w: 65, h: 80 }, { x: 808, y: 248, w: 61, h: 68 },
  { x: 16, y: 337, w: 92, h: 81 }, { x: 132, y: 329, w: 79, h: 91 },
  { x: 245, y: 333, w: 77, h: 87 }, { x: 359, y: 327, w: 80, h: 87 },
  { x: 471, y: 331, w: 83, h: 89 }, { x: 568, y: 325, w: 104, h: 99 },
  { x: 685, y: 329, w: 88, h: 95 }, { x: 798, y: 333, w: 69, h: 87 },
];

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function intersects(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function emptySavedLevel(): SavedLevel {
  return { completed: false, bestPins: 0, crown: false, bestTime: null, grade: "" };
}

export class GoldenGroveGame {
  readonly audio = new GameAudio();

  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly callbacks: GameCallbacks;
  private readonly input: InputState;
  private readonly sprite = new Image();
  private spriteReady = false;
  private state: GameState = "ready";
  private currentLevelIndex = 0;
  private level: LevelDefinition = LEVELS[0];
  private player: Player = this.createPlayer(LEVELS[0].spawnX, true);
  private previousPlayerX = this.player.x;
  private previousPlayerY = this.player.y;
  private items: Collectible[] = freshCollectibles(this.level);
  private critters: GroveCritter[] = freshCritters(this.level);
  private runtimePlatforms: RuntimePlatform[] = [];
  private particles: Particle[] = [];
  private popupTexts: PopupText[] = [];
  private progress: SavedProgress = this.loadProgress();
  private score = 0;
  private pins = 0;
  private fruits = 0;
  private lives = 3;
  private elapsed = 0;
  private visualTime = 0;
  private checkpointReached = false;
  private crownFound = false;
  private respawnX = this.level.spawnX;
  private cameraX = 0;
  private previousCameraX = 0;
  private cartX = 0;
  private previousCartX = 0;
  private cartCaught = false;
  private viewWidth = 1280;
  private renderScale = 1;
  private dpr = 1;
  private lastFrame = performance.now();
  private accumulator = 0;
  private shake = 0;
  private levelIntroTimer = 0;
  private reducedMotion = localStorage.getItem("uppg-reduced-motion") === "true"
    || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private debugMode = new URLSearchParams(window.location.search).get("dev") === "1";
  private showDebugHitboxes = false;

  constructor(canvas: HTMLCanvasElement, callbacks: GameCallbacks) {
    this.canvas = canvas;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser does not support the Canvas 2D API.");
    this.context = context;
    this.callbacks = callbacks;
    this.input = new InputState(
      () => this.togglePause(),
      () => this.controllerPrimary(),
      (direction) => this.controllerNavigate(direction),
    );
    this.currentLevelIndex = this.firstIncompleteLevel();
    this.level = LEVELS[this.currentLevelIndex];
    this.player = this.createPlayer(this.level.spawnX, true);
    this.items = freshCollectibles(this.level);
    this.critters = freshCritters(this.level);
    this.runtimePlatforms = this.makeRuntimePlatforms();
    this.audio.setTheme(this.level.theme.scenery);

    this.sprite.onload = () => {
      this.spriteReady = true;
    };
    this.sprite.src = "/assets/ultimate-pinapple-protagonist.png";

    window.addEventListener("keydown", (event) => {
      if (event.repeat) return;
      if (event.code === "F8") {
        event.preventDefault();
        this.setDebugMode(!this.debugMode);
      } else if (!this.debugMode) {
        return;
      } else if (event.code === "Digit1" || event.code === "Numpad1") {
        event.preventDefault();
        this.devSkip(1);
      } else if (event.code === "Digit2" || event.code === "Numpad2") {
        event.preventDefault();
        this.devSkip(-1);
      } else if (event.code === "F7") {
        event.preventDefault();
        this.showDebugHitboxes = !this.showDebugHitboxes;
        this.callbacks.onToast(`DEV: hitboxes ${this.showDebugHitboxes ? "shown" : "hidden"}`);
      } else if (event.code === "KeyR") {
        event.preventDefault();
        this.beginLevel(this.currentLevelIndex, false);
        this.callbacks.onToast("DEV: level restarted");
      }
    });
    window.addEventListener("resize", () => this.resize());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.state === "playing") this.pause();
    });
    this.resize();
    requestAnimationFrame((time) => this.frame(time));
  }

  get currentState(): GameState {
    return this.state;
  }

  get muted(): boolean {
    return this.audio.isMuted;
  }

  get debugEnabled(): boolean {
    return this.debugMode;
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
    localStorage.setItem("uppg-reduced-motion", String(enabled));
    if (enabled) this.shake = 0;
  }

  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    if (!enabled) this.showDebugHitboxes = false;
    this.callbacks.onDebugChange?.(enabled);
    this.callbacks.onToast(`Developer testing ${enabled ? "enabled" : "disabled"}`);
  }

  resetProgress(): void {
    localStorage.removeItem(PROGRESS_KEY);
    this.progress = { version: 2, levels: ALL_LEVELS.map(() => emptySavedLevel()) };
    this.currentLevelIndex = 0;
    this.level = LEVELS[0];
    this.resetLevel();
    this.openMap();
    this.callbacks.onToast("Local Golden Grove progress reset.");
  }

  start(): void {
    this.audio.unlock();
    if (this.state === "paused") {
      this.state = "playing";
      this.lastFrame = performance.now();
      this.callbacks.onStateChange(this.state, this.stats());
      return;
    }
    if (this.state === "won") {
      const nextIndex = this.currentLevelIndex + 1;
      if (!this.level.bonus && nextIndex < LEVELS.length && this.isUnlocked(nextIndex)) {
        this.beginLevel(nextIndex, true);
      } else {
        this.openMap();
      }
      return;
    }
    if (this.state === "ready" || this.state === "worldcomplete") {
      this.openMap();
      return;
    }
    this.beginLevel(this.currentLevelIndex, this.state === "map");
  }

  openMap(): void {
    this.state = "map";
    this.input.clear();
    this.callbacks.onStateChange(this.state, this.stats());
  }

  selectLevel(index: number): void {
    if (!this.isUnlocked(index)) {
      this.callbacks.onToast("Finish the previous level to unlock this path.");
      return;
    }
    this.beginLevel(index, true);
  }

  retryLevel(): void {
    this.audio.unlock();
    this.beginLevel(this.currentLevelIndex, true);
  }

  togglePause(): void {
    if (this.state === "playing") this.pause();
    else if (this.state === "paused") this.start();
  }

  pause(): void {
    if (this.state !== "playing") return;
    this.state = "paused";
    this.input.clear();
    this.callbacks.onStateChange(this.state, this.stats());
  }

  private controllerPrimary(): void {
    if (this.state === "playing") return;
    if (this.state === "map") {
      if (this.isUnlocked(this.currentLevelIndex)) this.beginLevel(this.currentLevelIndex, true);
      return;
    }
    this.start();
  }

  private controllerNavigate(direction: -1 | 1): void {
    if (this.state !== "map") return;
    const unlocked = ALL_LEVELS.reduce<number[]>((indices, _level, index) => {
      if (this.isUnlocked(index)) indices.push(index);
      return indices;
    }, []);
    const position = Math.max(0, unlocked.indexOf(this.currentLevelIndex));
    const nextPosition = clamp(position + direction, 0, unlocked.length - 1);
    const nextIndex = unlocked[nextPosition] ?? this.currentLevelIndex;
    if (nextIndex === this.currentLevelIndex) return;
    this.currentLevelIndex = nextIndex;
    this.level = ALL_LEVELS[nextIndex];
    this.callbacks.onStateChange(this.state, this.stats());
  }

  private devSkip(direction: -1 | 1): void {
    const next = clamp(this.currentLevelIndex + direction, 0, ALL_LEVELS.length - 1);
    if (next === this.currentLevelIndex) {
      this.callbacks.onToast(direction > 0 ? "DEV: already at the final level" : "DEV: already at level 1");
      return;
    }
    this.beginLevel(next, false);
    this.callbacks.onToast(this.level.bonus ? `DEV: Bonus — ${this.level.name}` : `DEV: Level ${next + 1} — ${this.level.name}`);
  }

  private beginLevel(index: number, announce: boolean): void {
    this.currentLevelIndex = clamp(index, 0, ALL_LEVELS.length - 1);
    this.level = ALL_LEVELS[this.currentLevelIndex];
    this.audio.setTheme(this.level.theme.scenery);
    this.resetLevel();
    this.state = "playing";
    this.lastFrame = performance.now();
    this.callbacks.onStateChange(this.state, this.stats());
    if (announce) {
      this.callbacks.onToast(
        this.level.bonus
          ? `Crown Vault bonus: ${this.level.name}`
          : `World ${this.level.worldNumber} · Level ${this.level.worldLevel}: ${this.level.name}`,
      );
    }
  }

  private resetLevel(): void {
    this.player = this.createPlayer(this.level.spawnX, true);
    this.items = freshCollectibles(this.level);
    this.critters = freshCritters(this.level);
    this.runtimePlatforms = this.makeRuntimePlatforms();
    this.particles = [];
    this.popupTexts = [];
    this.score = 0;
    this.pins = 0;
    this.fruits = 0;
    this.lives = 3;
    this.elapsed = 0;
    this.checkpointReached = false;
    this.crownFound = false;
    this.respawnX = this.level.spawnX;
    this.cartX = this.level.cart?.startX ?? 0;
    this.previousCartX = this.cartX;
    this.cartCaught = false;
    this.cameraX = 0;
    this.accumulator = 0;
    this.shake = 0;
    this.levelIntroTimer = this.reducedMotion ? 1.4 : 2.6;
    this.input.clear();
    this.syncInterpolation();
  }

  private makeRuntimePlatforms(): RuntimePlatform[] {
    return this.level.platforms.map((platform, index) => ({
      ...platform,
      runtimeId: platform.id ?? `${this.level.id}-platform-${index}`,
      originX: platform.x,
      originY: platform.y,
      active: true,
      broken: false,
      dx: 0,
      dy: 0,
    }));
  }

  private createPlayer(x: number, useLevelSpawnY = false): Player {
    return {
      x,
      y: useLevelSpawnY && this.level.spawnY !== undefined
        ? this.level.spawnY
        : this.level.groundY - NORMAL_HEIGHT,
      w: NORMAL_WIDTH,
      h: NORMAL_HEIGHT,
      vx: 0,
      vy: 0,
      facing: 1,
      onGround: false,
      coyote: 0,
      jumpBuffer: 0,
      power: null,
      powerTimer: 0,
      extraJumpUsed: false,
      invincible: 0,
      respawnTimer: 0,
      landingTimer: 0,
      runCycle: 0,
      airTime: 0,
      gliding: false,
      standingPlatform: null,
      powerFlash: 0,
    };
  }

  private frame(now: number): void {
    const delta = Math.min((now - this.lastFrame) / 1000, 0.05);
    this.lastFrame = now;
    this.visualTime += delta;
    this.input.pollGamepad();

    if (this.state === "playing") {
      this.accumulator += delta;
      while (this.accumulator >= FIXED_STEP) {
        this.update(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
      }
      this.audio.updateMusic(true);
    } else {
      this.accumulator = 0;
      this.audio.updateMusic(false);
    }

    this.render();
    requestAnimationFrame((time) => this.frame(time));
  }

  private update(dt: number): void {
    this.previousPlayerX = this.player.x;
    this.previousPlayerY = this.player.y;
    this.previousCameraX = this.cameraX;
    this.previousCartX = this.cartX;
    const player = this.player;
    this.elapsed += dt;
    player.invincible = Math.max(0, player.invincible - dt);
    player.landingTimer = Math.max(0, player.landingTimer - dt);
    player.powerFlash = Math.max(0, player.powerFlash - dt);
    if (player.powerTimer > 0) {
      player.powerTimer = Math.max(0, player.powerTimer - dt);
      if (player.powerTimer === 0 && (player.power === "bubble" || player.power === "glow")) {
        const expired = player.power;
        this.applyPower(null);
        this.callbacks.onToast(expired === "bubble" ? "Fizz Bubble popped" : "Golden Glow faded");
      }
    }
    this.levelIntroTimer = Math.max(0, this.levelIntroTimer - dt);
    this.shake = Math.max(0, this.shake - dt * 2.8);
    this.updatePlatforms();
    this.updateCart(dt);

    if (player.respawnTimer > 0) {
      player.respawnTimer -= dt;
      this.updateParticles(dt);
      this.updatePopupTexts(dt);
      if (player.respawnTimer <= 0 && this.state === "playing") this.respawn();
      return;
    }

    if (this.input.consumeJump()) player.jumpBuffer = 0.13;
    player.jumpBuffer = Math.max(0, player.jumpBuffer - dt);
    player.coyote = player.onGround ? 0.1 : Math.max(0, player.coyote - dt);

    const direction = Number(this.input.right) - Number(this.input.left);
    const glowBoost = player.power === "glow" ? 1.28 : 1;
    const maxSpeed = (this.input.run ? 430 : 315) * glowBoost;
    const acceleration = (player.onGround ? 2350 : 1500) * (player.power === "glow" ? 1.18 : 1);

    if (direction !== 0) {
      player.vx += direction * acceleration * dt;
      player.vx = clamp(player.vx, -maxSpeed, maxSpeed);
      player.facing = direction as -1 | 1;
    } else {
      const drag = player.onGround ? 1900 : 380;
      player.vx = Math.sign(player.vx) * Math.max(0, Math.abs(player.vx) - drag * dt);
    }

    if (player.jumpBuffer > 0) {
      if (player.coyote > 0) {
        this.performJump(false);
      } else if (player.power === "bubble" && !player.extraJumpUsed) {
        player.extraJumpUsed = true;
        this.performJump(true);
        this.burst(player.x + player.w / 2, player.y + player.h / 2, "#74e5ff", 12);
      }
    }

    if (this.input.consumeJumpRelease() && player.vy < -250) player.vy *= 0.52;
    player.gliding = false;
    player.vy = Math.min(player.vy + GRAVITY * dt, 1180);
    this.movePlayerX(dt);
    this.movePlayerY(dt);
    this.checkSprings();

    if (player.onGround) {
      player.airTime = 0;
      player.gliding = false;
      if (Math.abs(player.vx) > 35) {
        const animationRate = 6.5 + Math.abs(player.vx) / 70;
        player.runCycle = (player.runCycle + animationRate * dt) % 8;
      } else {
        player.runCycle = 0;
      }
    } else {
      player.airTime += dt;
    }

    if (Math.abs(player.vx) > 285 && player.onGround && Math.random() < (player.power === "glow" ? 0.24 : 0.09)) {
      this.addParticle(
        player.x + player.w / 2 - player.facing * 22,
        player.y + player.h,
        player.power === "glow" ? "#ffe15a" : "#fff2c7",
        player.power === "glow" ? 5 : 3,
        player.power === "glow" ? 145 : 90,
      );
    }

    this.updateCritters(dt);
    this.collectItems();
    this.checkHazards();
    this.checkCheckpoint();
    this.checkGoal();
    this.updateParticles(dt);
    this.updatePopupTexts(dt);

    const targetCamera = clamp(
      player.x - this.viewWidth * 0.36,
      0,
      Math.max(0, this.level.worldWidth - this.viewWidth),
    );
    this.cameraX += (targetCamera - this.cameraX) * (1 - Math.exp(-7 * dt));
  }

  private updatePlatforms(): void {
    for (const platform of this.runtimePlatforms) {
      const oldX = platform.x;
      const oldY = platform.y;
      platform.active = !platform.broken;
      if (platform.motion === "move-x") {
        platform.x = platform.originX + Math.sin(this.elapsed * (platform.speed ?? 1) + (platform.phase ?? 0)) * (platform.range ?? 90);
      } else if (platform.motion === "move-y") {
        platform.y = platform.originY + Math.sin(this.elapsed * (platform.speed ?? 1) + (platform.phase ?? 0)) * (platform.range ?? 90);
      } else if (platform.motion === "vanish") {
        const cycle = (this.elapsed + (platform.phase ?? 0)) % 3.2;
        platform.active = cycle < 2.35 && !platform.broken;
      }
      platform.dx = platform.x - oldX;
      platform.dy = platform.y - oldY;

      if (this.player.standingPlatform === platform.runtimeId) {
        if (platform.active) {
          this.player.x += platform.dx;
          this.player.y += platform.dy;
        } else {
          this.player.standingPlatform = null;
          this.player.onGround = false;
        }
      }
    }
  }

  private updateCart(dt: number): void {
    if (!this.level.cart) return;
    const finish = this.level.goalX - 72;
    if (!this.cartCaught) {
      this.cartX = Math.min(finish, this.cartX + this.level.cart.speed * dt);
      if (this.player.x + this.player.w < this.cartX - 58) return;

      this.cartCaught = true;
      this.score += 500;
      this.audio.play("checkpoint");
      this.burst(this.cartX, this.level.groundY - 78, "#ffe05d", 24);
      this.callbacks.onToast(
        this.level.cart.kind === "soda-float"
          ? "Soda float caught! Guide it to the lighthouse!"
          : "Cart caught! Push it to the grove gate!",
      );
    }

    const pushTarget = Math.min(
      finish,
      Math.max(this.cartX, this.player.x + this.player.w + 76),
    );
    this.cartX += (pushTarget - this.cartX) * (1 - Math.exp(-14 * dt));
  }

  private performJump(extra: boolean): void {
    const glowJump = this.player.power === "glow";
    this.player.vy = extra ? -780 : glowJump ? -940 : -820;
    this.player.onGround = false;
    this.player.standingPlatform = null;
    this.player.coyote = 0;
    this.player.jumpBuffer = 0;
    this.player.airTime = 0;
    this.audio.play(extra ? "bounce" : "jump");
  }

  private movePlayerX(dt: number): void {
    const player = this.player;
    player.x += player.vx * dt;
    player.x = clamp(player.x, 0, this.level.worldWidth - player.w);

    for (const platform of this.runtimePlatforms) {
      if (!platform.active || !intersects(player, platform)) continue;
      if (player.vx > 0) player.x = platform.x - player.w;
      else if (player.vx < 0) player.x = platform.x + platform.w;
      player.vx = 0;
    }

    for (const gate of this.level.powerGates ?? []) {
      if (player.power === gate.requires || !intersects(player, gate)) continue;
      if (player.vx > 0) player.x = gate.x - player.w;
      else if (player.vx < 0) player.x = gate.x + gate.w;
      player.vx = 0;
    }
  }

  private movePlayerY(dt: number): void {
    const player = this.player;
    const previousY = player.y;
    const wasGrounded = player.onGround;
    player.onGround = false;
    player.standingPlatform = null;
    player.y += player.vy * dt;

    for (const platform of this.runtimePlatforms) {
      if (!platform.active || !intersects(player, platform)) continue;
      const previousBottom = previousY + player.h;
      const previousTop = previousY;
      if (player.vy >= 0 && previousBottom <= platform.y + 8) {
        player.y = platform.y - player.h;
        player.vy = 0;
        player.onGround = true;
        player.standingPlatform = platform.runtimeId;
        player.extraJumpUsed = false;
        if (!wasGrounded) {
          player.landingTimer = 0.11;
          if (Math.abs(player.vx) > 210) {
            this.burst(player.x + player.w / 2, player.y + player.h, "#f5e8bc", 5);
          }
        }
      } else if (player.vy < 0 && previousTop >= platform.y + platform.h - 8) {
        if (platform.kind === "breakable") {
          platform.broken = true;
          platform.active = false;
          this.score += 50;
          this.burst(platform.x + platform.w / 2, platform.y + platform.h / 2, "#d99a57", 13);
          this.audio.play("bounce");
        } else {
          player.y = platform.y + platform.h;
          player.vy = 55;
        }
      }
    }

    if (!player.onGround && player.vy >= 0) this.landOnSlope(previousY, wasGrounded);
    if (player.y > this.level.worldHeight + 90) this.takeDamage(true);
  }

  private landOnSlope(previousY: number, wasGrounded: boolean): void {
    const player = this.player;
    const centerX = player.x + player.w / 2;
    for (const slope of this.level.slopes) {
      if (centerX < slope.x || centerX > slope.x + slope.w) continue;
      const surfaceY = this.slopeSurface(slope, centerX);
      const previousBottom = previousY + player.h;
      const bottom = player.y + player.h;
      if (previousBottom > surfaceY + 14 || bottom < surfaceY) continue;
      player.y = surfaceY - player.h;
      player.vy = 0;
      player.onGround = true;
      player.extraJumpUsed = false;
      if (!wasGrounded) player.landingTimer = 0.08;
      return;
    }
  }

  private slopeSurface(slope: Slope, worldX: number): number {
    const amount = clamp((worldX - slope.x) / slope.w, 0, 1);
    return slope.direction === "up" ? slope.y + slope.h - amount * slope.h : slope.y + amount * slope.h;
  }

  private checkSprings(): void {
    if (this.player.vy < 0) return;
    const feet = {
      x: this.player.x + 5,
      y: this.player.y + this.player.h - 16,
      w: this.player.w - 10,
      h: 20,
    };
    for (const spring of this.level.springs) {
      const hitbox = { x: spring.x, y: spring.y, w: spring.w ?? 54, h: 30 };
      if (!intersects(feet, hitbox)) continue;
      this.player.vy = -(spring.strength ?? 1030);
      this.player.onGround = false;
      this.player.standingPlatform = null;
      this.player.airTime = 0;
      this.audio.play("bounce");
      this.burst(spring.x + hitbox.w / 2, spring.y, "#ffe45d", 10);
      return;
    }
  }

  private collectItems(): void {
    const playerCenterX = this.player.x + this.player.w / 2;
    const playerCenterY = this.player.y + this.player.h / 2;

    for (const item of this.items) {
      if (item.collected) continue;
      const dx = item.x - playerCenterX;
      const dy = item.y - playerCenterY;
      const radius = item.kind === "golden-pin" || item.kind === "crown-piece" ? 49 : 40;
      if (dx * dx + dy * dy > radius * radius) continue;

      item.collected = true;
      if (item.kind === "fruit") {
        this.fruits += 1;
        this.score += 25;
        this.audio.play("fruit");
        this.burst(item.x, item.y, "#ffdf52", 7);
        this.addPopup(item.x, item.y - 14, "+25", "#fff4a3");
      } else if (item.kind === "golden-pin") {
        this.pins += 1;
        this.score += 500;
        this.audio.play("pin");
        this.burst(item.x, item.y, "#ffe45d", 22);
        this.addPopup(item.x, item.y - 22, "+500", "#ffe45d");
        this.callbacks.onToast(`Golden Pin ${this.pins} of 3 found!`);
      } else if (item.kind === "crown-piece") {
        const goldenWasUnlocked = this.goldenHeroUnlocked();
        this.crownFound = true;
        this.score += 750;
        this.audio.play("pin");
        this.burst(item.x, item.y, "#fff28a", 30);
        this.addPopup(item.x, item.y - 24, "SECRET +750", "#fff28a");
        this.callbacks.onToast(
          !goldenWasUnlocked && this.goldenHeroUnlocked()
            ? "All ten Crown Pieces found — Golden Hero unlocked!"
            : "Secret Crown Piece found!",
        );
      } else {
        const power: PowerUpKind =
          item.kind === "fizz-bubble"
            ? "bubble"
            : item.kind === "golden-glow"
              ? "glow"
              : item.kind === "pin-shield"
                ? "shield"
                : "mini";
        this.applyPower(power);
        this.score += 150;
        this.audio.play("power");
        const color =
          power === "bubble" ? "#73e7ff" : power === "glow" ? "#ffe15a" : power === "shield" ? "#6f9dff" : "#ff9f53";
        this.burst(item.x, item.y, color, 18);
        this.addPopup(item.x, item.y - 22, "POWER UP!", color);
        const message =
          power === "bubble"
            ? "Fizz Bubble! Air bounce + tide rescue for 14 seconds"
            : power === "glow"
              ? "Golden Glow! Faster, higher and hazard-proof for 10 seconds"
              : power === "shield"
                ? "Pin Shield! Your next hit is blocked"
                : "Mini Slice! Tiny tunnels are now open";
        this.callbacks.onToast(message);
      }
    }
  }

  private applyPower(power: PowerUpKind | null): void {
    const feet = this.player.y + this.player.h;
    this.player.power = power;
    this.player.powerTimer = power === "bubble" ? 14 : power === "glow" ? 10 : 0;
    this.player.extraJumpUsed = false;
    this.player.powerFlash = power ? 0.62 : 0;
    const mini = power === "mini";
    this.player.w = mini ? MINI_WIDTH : NORMAL_WIDTH;
    this.player.h = mini ? MINI_HEIGHT : NORMAL_HEIGHT;
    this.player.y = feet - this.player.h;
  }

  private updateCritters(dt: number): void {
    for (const critter of this.critters) {
      if (critter.defeated) {
        critter.defeatTimer = Math.max(0, (critter.defeatTimer ?? 0) - dt);
        continue;
      }

      if (critter.stunned > 0) {
        critter.stunned = Math.max(0, critter.stunned - dt);
      } else {
        critter.x += critter.direction * (critter.speed ?? 58) * dt;
        if (critter.x <= critter.minX || critter.x >= critter.maxX) {
          critter.x = clamp(critter.x, critter.minX, critter.maxX);
          critter.direction = critter.direction === 1 ? -1 : 1;
        }
      }

      const critterTop = critter.y - CRITTER_HEIGHT;
      const hitbox = {
        x: critter.x - CRITTER_HALF_WIDTH,
        y: critterTop,
        w: CRITTER_HALF_WIDTH * 2,
        h: CRITTER_HEIGHT + 3,
      };
      if (!intersects(this.player, hitbox) || this.player.invincible > 0) continue;

      if (this.player.power === "glow") {
        critter.defeated = true;
        critter.defeatTimer = CRITTER_DEFEAT_TIME;
        this.score += 250;
        this.audio.play("power");
        this.burst(critter.x, critter.y - 24, "#ffe15a", 18);
        this.addPopup(critter.x, critter.y - 62, "GLOW! +250", "#ffe15a");
        continue;
      }

      const previousPlayerBottom = this.previousPlayerY + this.player.h;
      const playerBottom = this.player.y + this.player.h;
      const stomped = this.player.vy > 120
        && previousPlayerBottom <= critterTop + 12
        && playerBottom >= critterTop;

      if (stomped) {
        this.player.y = critterTop - this.player.h;
        this.player.vy = -590;
        this.player.onGround = false;
        this.player.standingPlatform = null;
        this.audio.play("bounce");
        if (critter.stunned > 0) {
          critter.defeated = true;
          critter.defeatTimer = CRITTER_DEFEAT_TIME;
          this.score += 250;
          this.burst(critter.x, critter.y - 24, "#ffda58", 18);
          this.addPopup(critter.x, critter.y - 62, "CLEAR! +250", "#ffda58");
          this.shake = this.reducedMotion ? 0 : Math.max(this.shake, 0.3);
          this.callbacks.onToast("Two stomps! Critter cleared.");
        } else {
          critter.stunned = CRITTER_STUN_TIME;
          this.score += 100;
          this.burst(critter.x, critter.y - 24, "#b8f071", 10);
          this.addPopup(critter.x, critter.y - 60, "STUN! +100", "#b8f071");
          this.shake = this.reducedMotion ? 0 : Math.max(this.shake, 0.14);
          this.callbacks.onToast("Critter stunned — bounce once more to clear it!");
        }
      } else if (critter.stunned <= 0) {
        this.takeDamage(false);
      }
    }
  }

  private checkHazards(): void {
    if (this.player.invincible > 0) return;
    if (this.player.power === "glow") return;
    const feet = {
      x: this.player.x + 8,
      y: this.player.y + this.player.h - 22,
      w: this.player.w - 16,
      h: 22,
    };
    if (this.level.hazards.some((hazard) => intersects(feet, hazard))) {
      this.takeDamage(false);
      return;
    }
    if (this.level.tide && !this.player.onGround && this.player.y + this.player.h > this.currentWaterY() + 8) {
      if (this.player.power === "bubble") {
        this.player.y = this.currentWaterY() - this.player.h - 6;
        this.player.vy = -690;
        this.player.extraJumpUsed = false;
        this.player.invincible = 0.45;
        this.audio.play("bounce");
        this.burst(this.player.x + this.player.w / 2, this.currentWaterY(), "#78e7ff", 16);
        this.callbacks.onToast("Fizz Bubble bounced you clear of the tide!");
      } else {
        this.takeDamage(true);
      }
    }
  }

  private takeDamage(fell: boolean): void {
    const player = this.player;
    if (player.respawnTimer > 0 || player.invincible > 0) return;

    this.audio.play("hurt");
    this.shake = 0.65;
    if (player.power === "shield" && !fell) {
      this.applyPower(null);
      player.invincible = 1.5;
      player.vy = -460;
      player.vx = -player.facing * 280;
      this.burst(player.x + player.w / 2, player.y + player.h / 2, "#77a7ff", 18);
      this.callbacks.onToast("Pin Shield blocked the hit!");
      return;
    }

    if (player.power) this.applyPower(null);

    this.lives -= 1;
    player.respawnTimer = 0.68;
    player.vx = 0;
    player.vy = fell ? 0 : -260;
    this.burst(player.x + player.w / 2, player.y + player.h / 2, "#ffb453", 14);
    if (this.lives <= 0) {
      window.setTimeout(() => {
        if (this.state !== "playing") return;
        this.state = "gameover";
        this.callbacks.onStateChange(this.state, this.stats());
      }, 520);
    }
  }

  private respawn(): void {
    this.player = this.createPlayer(this.respawnX);
    this.player.invincible = 1.4;
    this.cameraX = clamp(
      this.respawnX - this.viewWidth * 0.35,
      0,
      Math.max(0, this.level.worldWidth - this.viewWidth),
    );
    this.syncInterpolation();
    this.callbacks.onToast(this.checkpointReached ? "Back at the checkpoint flag" : "Try that bounce again!");
  }

  private syncInterpolation(): void {
    this.previousPlayerX = this.player.x;
    this.previousPlayerY = this.player.y;
    this.previousCameraX = this.cameraX;
    this.previousCartX = this.cartX;
  }

  private checkCheckpoint(): void {
    if (this.checkpointReached || this.player.x < this.level.checkpointX) return;
    this.checkpointReached = true;
    this.respawnX = this.level.respawnX;
    this.score += 250;
    this.audio.play("checkpoint");
    this.addPopup(this.level.checkpointX + 24, this.level.groundY - 165, "CHECKPOINT +250", "#ffdf52");
    this.callbacks.onToast("Checkpoint reached!");
    this.burst(this.level.checkpointX, this.level.groundY - 85, "#ffdf52", 18);
  }

  private checkGoal(): void {
    if (this.player.x + this.player.w < this.level.goalX || this.state !== "playing") return;
    this.player.vx = 0;
    this.score += this.pins * 500 + this.lives * 250 + (this.crownFound ? 500 : 0);
    this.saveCompletion();
    const nextLevel = LEVELS[this.currentLevelIndex + 1];
    const finishedWorld = !nextLevel || nextLevel.worldNumber !== this.level.worldNumber;
    this.state = this.level.bonus ? "won" : finishedWorld ? "worldcomplete" : "won";
    this.audio.play("win");
    this.burst(this.level.goalX + 25, this.level.groundY - 145, "#ffdf52", 40);
    window.setTimeout(() => this.callbacks.onStateChange(this.state, this.stats()), 650);
  }

  private saveCompletion(): void {
    const record = this.progress.levels[this.currentLevelIndex] ?? emptySavedLevel();
    const grade: SavedLevel["grade"] =
      this.level.bonus && this.pins === 3 && this.elapsed <= this.level.parTime
        ? "Gold"
        : this.level.bonus && this.pins === 3
          ? "Silver"
          : this.pins === 3 && this.crownFound && this.elapsed <= this.level.parTime
        ? "Gold"
        : this.pins === 3 && this.crownFound
          ? "Silver"
          : "Bronze";
    record.completed = true;
    record.bestPins = Math.max(record.bestPins, this.pins);
    record.crown ||= this.crownFound;
    record.bestTime = record.bestTime === null ? this.elapsed : Math.min(record.bestTime, this.elapsed);
    if (this.gradeRank(grade) > this.gradeRank(record.grade)) record.grade = grade;
    this.progress.levels[this.currentLevelIndex] = record;
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(this.progress));
  }

  private gradeRank(grade: SavedLevel["grade"]): number {
    return grade === "Gold" ? 3 : grade === "Silver" ? 2 : grade === "Bronze" ? 1 : 0;
  }

  private loadProgress(): SavedProgress {
    try {
      const parsed = JSON.parse(localStorage.getItem(PROGRESS_KEY) ?? "") as Partial<SavedProgress>;
      if (parsed.version === 2 && Array.isArray(parsed.levels)) {
        return {
          version: 2,
          levels: ALL_LEVELS.map((_, index) => ({ ...emptySavedLevel(), ...parsed.levels?.[index] })),
        };
      }
    } catch {
      // A damaged local save should never stop the game from loading.
    }
    return { version: 2, levels: ALL_LEVELS.map(() => emptySavedLevel()) };
  }

  private firstIncompleteLevel(): number {
    const index = this.progress.levels.slice(0, LEVELS.length).findIndex((level) => !level.completed);
    return index < 0 ? 0 : index;
  }

  private isUnlocked(index: number): boolean {
    const level = ALL_LEVELS[index];
    if (!level) return false;
    if (level.bonus) return this.campaignCrownCount() >= (level.unlockCrowns ?? 5);
    return index === 0 || Boolean(this.progress.levels[index - 1]?.completed);
  }

  private campaignCrownCount(): number {
    let crowns = this.progress.levels.slice(0, LEVELS.length).filter((level) => level.crown).length;
    const currentSaved = this.progress.levels[this.currentLevelIndex];
    if (!this.level.bonus && this.crownFound && !currentSaved?.crown) crowns += 1;
    return crowns;
  }

  private goldenHeroUnlocked(): boolean {
    return this.campaignCrownCount() === LEVELS.length;
  }

  private currentWaterY(): number {
    if (!this.level.tide) return 640;
    const { lowY, highY, speed, phase = 0 } = this.level.tide;
    const amount = (Math.sin(this.elapsed * speed + phase) + 1) / 2;
    return lowY + (highY - lowY) * amount;
  }

  private updateParticles(dt: number): void {
    for (const particle of this.particles) {
      particle.life -= dt;
      particle.vy += particle.gravity * dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);
  }

  private updatePopupTexts(dt: number): void {
    for (const popup of this.popupTexts) {
      popup.life -= dt;
      popup.y -= (this.reducedMotion ? 12 : 34) * dt;
    }
    this.popupTexts = this.popupTexts.filter((popup) => popup.life > 0);
  }

  private addPopup(x: number, y: number, text: string, color: string): void {
    this.popupTexts.push({ x, y, text, color, life: 0.9, maxLife: 0.9 });
  }

  private addParticle(x: number, y: number, color: string, size: number, speed: number): void {
    const angle = Math.random() * Math.PI * 2;
    const velocity = speed * (0.45 + Math.random() * 0.65);
    this.particles.push({
      x,
      y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity - 35,
      life: 0.4 + Math.random() * 0.35,
      maxLife: 0.75,
      size: size * (0.7 + Math.random() * 0.7),
      color,
      gravity: 180,
    });
  }

  private burst(x: number, y: number, color: string, count: number): void {
    const total = this.reducedMotion ? Math.min(6, count) : count;
    for (let index = 0; index < total; index += 1) this.addParticle(x, y, color, 4.5, 210);
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.round(rect.width * this.dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * this.dpr));
    this.renderScale = rect.height / this.level.worldHeight;
    this.viewWidth = rect.width / this.renderScale;
  }

  private render(): void {
    const ctx = this.context;
    const width = this.canvas.width;
    const height = this.canvas.height;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.setTransform(this.dpr * this.renderScale, 0, 0, this.dpr * this.renderScale, 0, 0);
    ctx.imageSmoothingEnabled = false;

    const interpolation = this.state === "playing" ? clamp(this.accumulator / FIXED_STEP, 0, 1) : 1;
    const renderPlayerX = this.previousPlayerX + (this.player.x - this.previousPlayerX) * interpolation;
    const renderPlayerY = this.previousPlayerY + (this.player.y - this.previousPlayerY) * interpolation;
    const renderCameraX = this.previousCameraX + (this.cameraX - this.previousCameraX) * interpolation;
    const renderCartX = this.previousCartX + (this.cartX - this.previousCartX) * interpolation;

    this.drawSky(ctx, renderCameraX);
    const shakeX = !this.reducedMotion && this.shake > 0 ? (Math.random() - 0.5) * this.shake * 14 : 0;
    const shakeY = !this.reducedMotion && this.shake > 0 ? (Math.random() - 0.5) * this.shake * 9 : 0;
    ctx.save();
    ctx.translate(-renderCameraX + shakeX, shakeY);
    this.drawWorld(ctx, renderPlayerX, renderPlayerY, renderCartX);
    ctx.restore();
    this.drawHud(ctx);
    this.drawLevelIntro(ctx);
  }

  private drawSky(ctx: CanvasRenderingContext2D, cameraX: number): void {
    const theme = this.level.theme;
    const sky = ctx.createLinearGradient(0, 0, 0, this.level.worldHeight);
    sky.addColorStop(0, theme.skyTop);
    sky.addColorStop(0.62, theme.haze);
    sky.addColorStop(1, theme.skyBottom);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, this.viewWidth, this.level.worldHeight);

    const evening = this.level.theme.scenery === "sunset" || this.level.theme.scenery === "marina";
    ctx.fillStyle = evening ? "rgba(255,213,118,0.88)" : "rgba(255,245,158,0.85)";
    ctx.beginPath();
    ctx.arc(this.viewWidth - 120, 112, evening ? 68 : 56, 0, Math.PI * 2);
    ctx.fill();

    this.drawCloudLayer(ctx, cameraX, 0.08, 105, "rgba(255,255,255,0.7)", 1);
    this.drawHills(ctx, cameraX, 0.13, 470, theme.hillLight, theme.hillDark, 230);
    this.drawHills(ctx, cameraX, 0.24, 535, theme.hillDark, theme.ground, 165);

    if (theme.scenery === "burrow") {
      ctx.fillStyle = "rgba(24,35,36,0.42)";
      ctx.fillRect(0, 0, this.viewWidth, 72);
      for (let x = -40; x < this.viewWidth + 80; x += 110) {
        ctx.beginPath();
        ctx.moveTo(x, 68);
        ctx.lineTo(x + 28, 128 + Math.sin(x) * 18);
        ctx.lineTo(x + 55, 68);
        ctx.closePath();
        ctx.fill();
      }
    } else if (theme.scenery === "treetop") {
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      for (let x = -80; x < this.viewWidth + 100; x += 190) {
        const drift = this.reducedMotion ? 0 : Math.sin(this.visualTime * 0.7 + x) * 18;
        ctx.beginPath();
        ctx.ellipse(x + drift, 255 + Math.sin(x * 0.04) * 45, 42, 13, -0.22, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (theme.scenery === "orchard") {
      ctx.strokeStyle = "rgba(255,255,255,0.28)";
      ctx.lineWidth = 3;
      for (let y = 170; y < 360; y += 62) {
        const drift = this.reducedMotion ? 0 : (this.visualTime * 34) % 180;
        for (let x = -160 + drift; x < this.viewWidth + 160; x += 230) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.quadraticCurveTo(x + 42, y - 9, x + 90, y);
          ctx.stroke();
        }
      }
    } else if (theme.scenery === "sunset" || theme.scenery === "marina" || theme.scenery === "crown-vault") {
      for (let x = 60; x < this.viewWidth; x += 125) {
        const pulse = this.reducedMotion ? 0.55 : 0.35 + Math.sin(this.visualTime * 2.4 + x) * 0.22;
        ctx.fillStyle = `rgba(255,239,133,${pulse})`;
        ctx.beginPath();
        ctx.arc(x, 205 + Math.sin(x * 0.08) * 95, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
    } else if (["shore", "fizz-falls", "tidepool", "boardwalk"].includes(theme.scenery)) {
      ctx.fillStyle = "rgba(255,255,255,0.42)";
      for (let x = 70; x < this.viewWidth + 80; x += 170) {
        const bubbleY = 210 + Math.sin(x * 0.031) * 82;
        const drift = this.reducedMotion ? 0 : Math.sin(this.visualTime * 1.7 + x) * 9;
        ctx.beginPath();
        ctx.arc(x + drift, bubbleY, 5 + (x % 4), 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = 3;
        ctx.stroke();
      }
      ctx.strokeStyle = "rgba(37,76,94,0.52)";
      ctx.lineWidth = 3;
      for (let x = 120; x < this.viewWidth; x += 310) {
        const flap = this.reducedMotion ? 0 : Math.sin(this.visualTime * 4 + x) * 4;
        ctx.beginPath();
        ctx.arc(x, 180 + flap, 13, Math.PI + 0.3, Math.PI * 2 - 0.25);
        ctx.arc(x + 24, 180 + flap, 13, Math.PI + 0.25, Math.PI * 2 - 0.3);
        ctx.stroke();
      }
    } else {
      ctx.fillStyle = "rgba(255,238,117,0.5)";
      for (let x = 90; x < this.viewWidth; x += 260) {
        const flutter = this.reducedMotion ? 0 : Math.sin(this.visualTime * 3 + x) * 7;
        ctx.beginPath();
        ctx.ellipse(x + flutter, 260 + Math.sin(x) * 42, 7, 3, 0.5, 0, Math.PI * 2);
        ctx.ellipse(x + 13 + flutter, 260 + Math.sin(x) * 42, 7, 3, -0.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawCloudLayer(
    ctx: CanvasRenderingContext2D,
    cameraX: number,
    parallax: number,
    baseY: number,
    color: string,
    scale: number,
  ): void {
    const spacing = 430;
    const offset = -((cameraX * parallax) % spacing);
    ctx.fillStyle = color;
    for (let x = offset - 160; x < this.viewWidth + 200; x += spacing) {
      const y = baseY + Math.sin((x + cameraX * parallax) * 0.01) * 35;
      ctx.beginPath();
      ctx.arc(x, y, 28 * scale, Math.PI, 0);
      ctx.arc(x + 35 * scale, y - 17 * scale, 38 * scale, Math.PI, 0);
      ctx.arc(x + 78 * scale, y, 30 * scale, Math.PI, 0);
      ctx.lineTo(x + 78 * scale, y + 22 * scale);
      ctx.lineTo(x, y + 22 * scale);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawHills(
    ctx: CanvasRenderingContext2D,
    cameraX: number,
    parallax: number,
    baseY: number,
    light: string,
    dark: string,
    radius: number,
  ): void {
    const spacing = radius * 1.45;
    const offset = -((cameraX * parallax) % spacing);
    for (let x = offset - radius; x < this.viewWidth + radius; x += spacing) {
      ctx.fillStyle = Math.round((x + cameraX * parallax) / spacing) % 2 === 0 ? light : dark;
      ctx.beginPath();
      ctx.moveTo(x - radius, this.level.worldHeight);
      ctx.quadraticCurveTo(x, baseY - radius, x + radius, this.level.worldHeight);
      ctx.closePath();
      ctx.fill();
    }
  }

  private drawWorld(
    ctx: CanvasRenderingContext2D,
    playerX: number,
    playerY: number,
    cartX: number,
  ): void {
    this.drawWater(ctx);
    this.drawScenery(ctx);
    for (const secret of this.level.secrets) this.drawSecretInterior(ctx, secret);
    for (const slope of this.level.slopes) this.drawSlope(ctx, slope);
    for (const platform of this.runtimePlatforms) if (platform.active) this.drawPlatform(ctx, platform);
    for (const gate of this.level.powerGates ?? []) this.drawPowerGate(ctx, gate);
    for (const hazard of this.level.hazards) this.drawHazard(ctx, hazard);
    for (const spring of this.level.springs) this.drawSpring(ctx, spring);
    this.drawCheckpoint(ctx);
    this.drawGoal(ctx);
    if (this.level.cart) this.drawCart(ctx, cartX);
    for (const item of this.items) if (!item.collected) this.drawCollectible(ctx, item);
    for (const critter of this.critters) {
      if (!critter.defeated || (critter.defeatTimer ?? 0) > 0) this.drawCritter(ctx, critter);
    }
    for (const particle of this.particles) this.drawParticle(ctx, particle);
    for (const popup of this.popupTexts) this.drawPopupText(ctx, popup);
    this.drawPlayer(ctx, playerX, playerY);
    for (const secret of this.level.secrets) this.drawSecretCover(ctx, secret, playerX, playerY);
    if (this.debugMode && this.showDebugHitboxes) this.drawDebugOverlay(ctx, playerX, playerY);
  }

  private drawWater(ctx: CanvasRenderingContext2D): void {
    const surfaceY = this.currentWaterY();
    const water = ctx.createLinearGradient(0, surfaceY - 10, 0, this.level.worldHeight);
    water.addColorStop(0, this.level.theme.waterTop);
    water.addColorStop(1, this.level.theme.waterBottom);
    ctx.fillStyle = water;
    ctx.fillRect(-200, surfaceY, this.level.worldWidth + 400, this.level.worldHeight - surfaceY + 30);
    ctx.strokeStyle = "rgba(255,255,255,0.68)";
    ctx.lineWidth = 5;
    for (let x = -200; x < this.level.worldWidth + 200; x += 42) {
      const y = surfaceY + 6 + Math.sin(this.visualTime * 3 + x * 0.05) * 4;
      ctx.beginPath();
      ctx.arc(x, y, 20, Math.PI, 0);
      ctx.stroke();
    }
    if (this.level.tide) {
      ctx.fillStyle = "rgba(255,255,255,0.48)";
      for (let x = -120; x < this.level.worldWidth + 160; x += 95) {
        const fizz = this.reducedMotion ? 0 : Math.sin(this.visualTime * 2 + x * 0.02) * 5;
        ctx.beginPath();
        ctx.arc(x, surfaceY + 22 + fizz, 4 + (Math.abs(x) % 3), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawScenery(ctx: CanvasRenderingContext2D): void {
    const scenery = this.level.theme.scenery;
    const seaside = ["shore", "fizz-falls", "tidepool", "boardwalk", "marina"].includes(scenery);
    const spacing = scenery === "treetop" || scenery === "crown-vault" ? 620 : seaside ? 720 : 830;
    for (let x = 80; x < this.level.worldWidth + 300; x += spacing) {
      if (scenery === "crown-vault") {
        ctx.fillStyle = "rgba(55,38,89,0.58)";
        ctx.beginPath();
        ctx.roundRect(x - 70, 285, 210, this.level.groundY - 285, 90);
        ctx.fill();
        ctx.fillStyle = "rgba(255,221,78,0.72)";
        ctx.beginPath();
        ctx.moveTo(x - 12, 345);
        ctx.lineTo(x + 2, 305);
        ctx.lineTo(x + 26, 335);
        ctx.lineTo(x + 52, 300);
        ctx.lineTo(x + 70, 345);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "rgba(255,241,160,0.42)";
        ctx.lineWidth = 5;
        ctx.strokeRect(x - 45, 405, 160, 205);
        continue;
      }
      if (seaside) {
        if (scenery === "shore") {
          ctx.fillStyle = "#8a5a3c";
          ctx.fillRect(x + 22, this.level.groundY - 115, 10, 115);
          ctx.fillStyle = Math.floor(x / spacing) % 2 ? "#ff6f61" : "#ffd84e";
          ctx.beginPath();
          ctx.moveTo(x - 45, this.level.groundY - 108);
          ctx.quadraticCurveTo(x + 28, this.level.groundY - 178, x + 100, this.level.groundY - 108);
          ctx.closePath();
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.85)";
          ctx.lineWidth = 7;
          ctx.beginPath();
          ctx.moveTo(x + 28, this.level.groundY - 168);
          ctx.lineTo(x + 28, this.level.groundY - 110);
          ctx.stroke();
        } else if (scenery === "fizz-falls") {
          ctx.fillStyle = "rgba(222,252,255,0.55)";
          ctx.beginPath();
          ctx.roundRect(x - 15, 310, 90, this.level.groundY - 310, 42);
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.72)";
          ctx.lineWidth = 8;
          for (let stream = 0; stream < 3; stream += 1) {
            ctx.beginPath();
            ctx.moveTo(x + 8 + stream * 22, 330);
            ctx.bezierCurveTo(x - 10 + stream * 30, 410, x + 35 + stream * 12, 500, x + 10 + stream * 24, this.level.groundY);
            ctx.stroke();
          }
        } else if (scenery === "tidepool") {
          ctx.fillStyle = "#6e6c71";
          ctx.beginPath();
          ctx.ellipse(x + 20, this.level.groundY - 28, 92, 42, -0.08, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = "#ef725f";
          for (let arm = 0; arm < 5; arm += 1) {
            ctx.beginPath();
            ctx.ellipse(x - 20 + arm * 20, this.level.groundY - 72 - (arm % 2) * 18, 9, 34, arm * 0.12, 0, Math.PI * 2);
            ctx.fill();
          }
        } else if (scenery === "boardwalk") {
          ctx.strokeStyle = "#75472f";
          ctx.lineWidth = 10;
          ctx.beginPath();
          ctx.moveTo(x - 70, this.level.groundY - 78);
          ctx.lineTo(x + 130, this.level.groundY - 78);
          for (let post = -50; post <= 110; post += 40) {
            ctx.moveTo(x + post, this.level.groundY - 108);
            ctx.lineTo(x + post, this.level.groundY);
          }
          ctx.stroke();
          ctx.fillStyle = Math.floor(x / spacing) % 2 ? "#53c4df" : "#f36b69";
          ctx.fillRect(x - 35, this.level.groundY - 160, 120, 42);
          ctx.fillStyle = "rgba(255,255,255,0.75)";
          for (let stripe = 0; stripe < 4; stripe += 1) ctx.fillRect(x - 30 + stripe * 30, this.level.groundY - 158, 13, 38);
        } else {
          ctx.fillStyle = "#f5efe0";
          ctx.beginPath();
          ctx.moveTo(x + 5, this.level.groundY);
          ctx.lineTo(x + 28, this.level.groundY - 255);
          ctx.lineTo(x + 64, this.level.groundY - 255);
          ctx.lineTo(x + 86, this.level.groundY);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = "#e75c55";
          ctx.fillRect(x + 20, this.level.groundY - 178, 55, 38);
          ctx.fillRect(x + 25, this.level.groundY - 105, 54, 38);
          ctx.fillStyle = "#ffe071";
          ctx.beginPath();
          ctx.arc(x + 46, this.level.groundY - 278, 20, 0, Math.PI * 2);
          ctx.fill();
        }
        continue;
      }

      if (scenery === "burrow") {
        ctx.strokeStyle = "#6d4b35";
        ctx.lineWidth = 18;
        ctx.beginPath();
        ctx.moveTo(x, this.level.groundY);
        ctx.bezierCurveTo(x - 80, 470, x + 130, 400, x + 85, 285);
        ctx.stroke();
        ctx.fillStyle = "#d7b15f";
        for (let dot = 0; dot < 5; dot += 1) {
          ctx.beginPath();
          ctx.arc(x - 25 + dot * 28, 520 - (dot % 2) * 35, 5, 0, Math.PI * 2);
          ctx.fill();
        }
        continue;
      }

      if (scenery === "orchard") {
        ctx.fillStyle = "#8b5230";
        ctx.fillRect(x + 10, this.level.groundY - 130, 32, 130);
        ctx.strokeStyle = "rgba(101,62,39,0.8)";
        ctx.lineWidth = 10;
        ctx.beginPath();
        ctx.moveTo(x - 78, this.level.groundY - 65);
        ctx.lineTo(x + 132, this.level.groundY - 65);
        ctx.moveTo(x - 78, this.level.groundY - 30);
        ctx.lineTo(x + 132, this.level.groundY - 30);
        ctx.stroke();
        ctx.fillStyle = "#3b9347";
        ctx.beginPath();
        ctx.arc(x + 25, this.level.groundY - 150, 76, 0, Math.PI * 2);
        ctx.arc(x - 32, this.level.groundY - 125, 48, 0, Math.PI * 2);
        ctx.arc(x + 82, this.level.groundY - 124, 50, 0, Math.PI * 2);
        ctx.fill();
        for (let fruit = 0; fruit < 8; fruit += 1) {
          ctx.fillStyle = fruit % 2 ? "#ffcf45" : "#f58233";
          ctx.beginPath();
          ctx.arc(x - 38 + (fruit % 4) * 38, this.level.groundY - 170 + Math.floor(fruit / 4) * 45, 8, 0, Math.PI * 2);
          ctx.fill();
        }
        continue;
      }

      const trunkHeight = scenery === "treetop" ? 290 : 155;
      const trunkWidth = scenery === "treetop" ? 54 : 28;
      ctx.fillStyle = scenery === "sunset" ? "#71402d" : "#8f552d";
      ctx.fillRect(x, this.level.groundY - trunkHeight, trunkWidth, trunkHeight);
      ctx.fillStyle = scenery === "sunset" ? "#406b4b" : "#2b844d";
      const crownY = this.level.groundY - trunkHeight - 25;
      const crownSize = scenery === "treetop" ? 108 : 70;
      ctx.beginPath();
      ctx.arc(x + trunkWidth / 2, crownY, crownSize, 0, Math.PI * 2);
      ctx.arc(x - crownSize * 0.55, crownY + 38, crownSize * 0.64, 0, Math.PI * 2);
      ctx.arc(x + crownSize * 0.72, crownY + 40, crownSize * 0.68, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = scenery === "sunset" ? "#ffd059" : "#ffb52d";
      for (let index = 0; index < 5; index += 1) {
        ctx.beginPath();
        ctx.arc(x - 28 + index * 29, crownY + 10 + (index % 2) * 27, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    if (seaside) {
      for (let x = 170; x < this.level.worldWidth; x += 290) {
        ctx.fillStyle = x % 580 === 170 ? "#f4a463" : "#f2d38a";
        ctx.beginPath();
        ctx.arc(x, this.level.groundY - 8, 11, Math.PI, 0);
        ctx.lineTo(x + 11, this.level.groundY);
        ctx.lineTo(x - 11, this.level.groundY);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.78)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x, this.level.groundY - 18);
        ctx.lineTo(x, this.level.groundY - 3);
        ctx.stroke();
      }
      return;
    }

    if (scenery === "sunset") {
      ctx.strokeStyle = "rgba(65,70,60,0.54)";
      ctx.lineWidth = 3;
      for (let x = 420; x < this.level.worldWidth; x += 980) {
        ctx.beginPath();
        ctx.moveTo(x, this.level.groundY - 160);
        ctx.quadraticCurveTo(x + 160, this.level.groundY - 118, x + 320, this.level.groundY - 160);
        ctx.stroke();
        for (let lamp = 0; lamp < 5; lamp += 1) {
          const lampX = x + 53 + lamp * 53;
          const lampY = this.level.groundY - 145 + Math.sin((lamp / 4) * Math.PI) * 20;
          ctx.fillStyle = "rgba(255,211,79,0.82)";
          ctx.beginPath();
          ctx.arc(lampX, lampY, 6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    for (let x = 180; x < this.level.worldWidth; x += 260) {
      ctx.strokeStyle = "#267a49";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(x, this.level.groundY);
      ctx.quadraticCurveTo(x - 8, 570, x + 10, 548);
      ctx.stroke();
      ctx.fillStyle = x % 520 === 180 ? "#ffcf4a" : "#ff7f6a";
      for (let petal = 0; petal < 5; petal += 1) {
        const angle = (petal / 5) * Math.PI * 2;
        ctx.beginPath();
        ctx.arc(x + 10 + Math.cos(angle) * 9, 542 + Math.sin(angle) * 9, 6, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawPlatform(ctx: CanvasRenderingContext2D, platform: RuntimePlatform): void {
    ctx.save();
    if (platform.motion === "vanish") {
      const cycle = (this.elapsed + (platform.phase ?? 0)) % 3.2;
      if (cycle > 1.82) ctx.globalAlpha = 0.45 + Math.sin(this.visualTime * 26) * 0.28;
    }

    if (platform.kind === "ground") {
      ctx.fillStyle = this.level.theme.ground;
      ctx.fillRect(platform.x, platform.y, platform.w, platform.h);
      ctx.fillStyle = this.level.theme.groundDetail;
      for (let x = platform.x + 24; x < platform.x + platform.w; x += 62) {
        for (let y = platform.y + 42; y < platform.y + platform.h; y += 46) ctx.fillRect(x, y, 22, 10);
      }
      ctx.fillStyle = this.level.theme.grass;
      ctx.fillRect(platform.x, platform.y, platform.w, 18);
      ctx.fillStyle = "rgba(255,255,255,0.2)";
      ctx.fillRect(platform.x, platform.y, platform.w, 8);
      ctx.restore();
      return;
    }

    if (platform.kind === "sand") {
      ctx.fillStyle = "#e7c676";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 17);
      ctx.fill();
      ctx.fillStyle = "rgba(255,247,190,0.72)";
      ctx.fillRect(platform.x + 14, platform.y + 6, platform.w - 28, 7);
      ctx.strokeStyle = "#b7894d";
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (platform.kind === "pier") {
      ctx.fillStyle = "#8d5738";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 8);
      ctx.fill();
      ctx.strokeStyle = "#513528";
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.strokeStyle = "rgba(245,197,119,0.58)";
      ctx.lineWidth = 3;
      for (let plank = platform.x + 26; plank < platform.x + platform.w; plank += 46) {
        ctx.beginPath();
        ctx.moveTo(plank, platform.y + 4);
        ctx.lineTo(plank, platform.y + platform.h - 4);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    if (platform.kind === "bottlecap") {
      ctx.fillStyle = "#ef5f57";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 15);
      ctx.fill();
      ctx.fillStyle = "#ffe36a";
      ctx.beginPath();
      ctx.roundRect(platform.x + 14, platform.y + 6, platform.w - 28, platform.h - 12, 10);
      ctx.fill();
      ctx.strokeStyle = "#943d3c";
      ctx.lineWidth = 4;
      for (let ridge = platform.x + 8; ridge < platform.x + platform.w; ridge += 18) {
        ctx.beginPath();
        ctx.moveTo(ridge, platform.y + 2);
        ctx.lineTo(ridge + 4, platform.y + 9);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    if (platform.kind === "coral") {
      ctx.fillStyle = "#e76f62";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y + 9, platform.w, platform.h - 9, 14);
      ctx.fill();
      ctx.fillStyle = "#ff9b77";
      for (let bulb = platform.x + 18; bulb < platform.x + platform.w; bulb += 34) {
        ctx.beginPath();
        ctx.arc(bulb, platform.y + 10, 14, Math.PI, 0);
        ctx.fill();
      }
      ctx.strokeStyle = "#9b4e50";
      ctx.lineWidth = 4;
      ctx.strokeRect(platform.x + 3, platform.y + 12, platform.w - 6, platform.h - 15);
      ctx.restore();
      return;
    }

    if (platform.kind === "bubble") {
      ctx.fillStyle = "rgba(132,232,247,0.32)";
      ctx.strokeStyle = "rgba(238,255,255,0.94)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 18);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.beginPath();
      ctx.ellipse(platform.x + 25, platform.y + 9, 12, 4, -0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }

    if (platform.kind === "log" || platform.kind === "wood") {
      ctx.fillStyle = platform.kind === "wood" ? "#915534" : "#7a4329";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 18);
      ctx.fill();
      ctx.fillStyle = "#bd7541";
      ctx.fillRect(platform.x + 18, platform.y + 7, platform.w - 36, 7);
      ctx.strokeStyle = "#4c2b22";
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (platform.kind === "stone" || platform.kind === "cave") {
      ctx.fillStyle = platform.kind === "cave" ? "#594b43" : "#667f86";
      ctx.beginPath();
      ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 10);
      ctx.fill();
      ctx.fillStyle = platform.kind === "cave" ? "#827064" : "#9bb3ad";
      ctx.fillRect(platform.x + 8, platform.y + 7, platform.w - 16, 9);
      ctx.strokeStyle = platform.kind === "cave" ? "#332f2c" : "#3d5962";
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (platform.kind === "leaf") {
      ctx.fillStyle = "#3f9b4e";
      ctx.beginPath();
      ctx.ellipse(platform.x + platform.w / 2, platform.y + platform.h / 2, platform.w / 2, platform.h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#226b3f";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(platform.x + 16, platform.y + platform.h / 2);
      ctx.lineTo(platform.x + platform.w - 16, platform.y + platform.h / 2);
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (platform.kind === "breakable") {
      ctx.fillStyle = "#c88445";
      ctx.fillRect(platform.x, platform.y, platform.w, platform.h);
      ctx.strokeStyle = "#79452d";
      ctx.lineWidth = 4;
      ctx.strokeRect(platform.x + 2, platform.y + 2, platform.w - 4, platform.h - 4);
      ctx.beginPath();
      ctx.moveTo(platform.x + 8, platform.y + 8);
      ctx.lineTo(platform.x + platform.w - 10, platform.y + platform.h - 10);
      ctx.moveTo(platform.x + platform.w - 8, platform.y + 8);
      ctx.lineTo(platform.x + 10, platform.y + platform.h - 10);
      ctx.stroke();
      ctx.restore();
      return;
    }

    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.beginPath();
    ctx.roundRect(platform.x, platform.y, platform.w, platform.h, 20);
    ctx.fill();
    ctx.fillStyle = "rgba(98,197,226,0.34)";
    ctx.fillRect(platform.x + 18, platform.y + platform.h - 10, platform.w - 36, 5);
    ctx.restore();
  }

  private drawPowerGate(ctx: CanvasRenderingContext2D, gate: PowerGate): void {
    const open = this.player.power === gate.requires;
    const colors: Record<PowerUpKind, string> = {
      bubble: "#63dff2",
      glow: "#ffe05a",
      mini: "#f28a3a",
      shield: "#78a4ff",
    };
    const color = colors[gate.requires];
    const pulse = this.reducedMotion ? 0.55 : 0.48 + Math.sin(this.visualTime * 4 + gate.x) * 0.18;
    ctx.save();
    ctx.globalAlpha = open ? 0.24 : 0.92;
    ctx.fillStyle = `rgba(24,51,60,${open ? 0.16 : 0.52})`;
    ctx.fillRect(gate.x, gate.y, gate.w, gate.h);
    ctx.strokeStyle = color;
    ctx.lineWidth = open ? 3 : 6;
    ctx.shadowColor = color;
    ctx.shadowBlur = open ? 8 : 18;
    ctx.strokeRect(gate.x, gate.y, gate.w, gate.h);
    ctx.shadowBlur = 0;
    ctx.fillStyle = color;
    for (let y = gate.y + 10; y < gate.y + gate.h - 4; y += 18) {
      ctx.globalAlpha = open ? 0.18 : pulse;
      ctx.fillRect(gate.x + 5, y, gate.w - 10, 7);
    }
    ctx.globalAlpha = open ? 0.44 : 1;
    ctx.translate(gate.x + gate.w / 2, gate.y - 11);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = color;
    ctx.font = "1000 11px Trebuchet MS";
    ctx.textAlign = "left";
    ctx.fillText(open ? "OPEN" : gate.label, -gate.h / 2, 4);
    ctx.restore();
  }

  private drawSecretCover(
    ctx: CanvasRenderingContext2D,
    secret: SecretArea,
    playerX: number,
    playerY: number,
  ): void {
    const playerCenterX = playerX + this.player.w / 2;
    const playerFeetY = playerY + this.player.h;
    const nearEntrance = secret.entrance === "left"
      ? playerCenterX >= secret.x - 38 && playerCenterX <= secret.x + secret.w
      : playerCenterX >= secret.x && playerCenterX <= secret.x + secret.w + 38;
    const inside = nearEntrance && playerFeetY > secret.y + secret.h - 96;
    const entranceWidth = 52;
    const entranceHeight = 76;
    const entranceX = secret.entrance === "left"
      ? secret.x - 2
      : secret.x + secret.w - entranceWidth + 2;
    const entranceY = secret.y + secret.h - entranceHeight;

    ctx.save();
    ctx.beginPath();
    ctx.rect(secret.x, secret.y, secret.w, secret.h);
    ctx.roundRect(entranceX, entranceY, entranceWidth, entranceHeight + 4, 22);
    if (inside) {
      ctx.roundRect(secret.x + 18, secret.y + 67, secret.w - 36, secret.h - 59, 22);
    }
    ctx.clip("evenodd");

    ctx.fillStyle = this.level.theme.ground;
    ctx.fillRect(secret.x, secret.y, secret.w, secret.h);
    ctx.fillStyle = this.level.theme.groundDetail;
    for (let x = secret.x + 24; x < secret.x + secret.w; x += 62) {
      for (let y = secret.y + 40; y < secret.y + secret.h; y += 46) {
        ctx.fillRect(x, y, 22, 10);
      }
    }
    ctx.fillStyle = this.level.theme.grass;
    ctx.fillRect(secret.x, secret.y, secret.w, 18);
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.fillRect(secret.x, secret.y, secret.w, 8);
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = inside ? "rgba(40,31,25,0.82)" : "rgba(45,35,28,0.5)";
    ctx.lineWidth = inside ? 8 : 5;
    ctx.beginPath();
    if (inside) {
      ctx.roundRect(secret.x + 18, secret.y + 67, secret.w - 36, secret.h - 59, 22);
    } else {
      ctx.moveTo(entranceX + (secret.entrance === "left" ? entranceWidth : 0), entranceY + 13);
      ctx.quadraticCurveTo(
        entranceX + entranceWidth / 2,
        entranceY - 5,
        entranceX + (secret.entrance === "left" ? 0 : entranceWidth),
        entranceY + 13,
      );
    }
    ctx.stroke();

    const rootX = secret.entrance === "left" ? secret.x + 22 : secret.x + secret.w - 22;
    ctx.strokeStyle = "rgba(84,61,39,0.82)";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(rootX, secret.y + secret.h - 74);
    ctx.quadraticCurveTo(
      rootX + (secret.entrance === "left" ? 14 : -14),
      secret.y + secret.h - 54,
      rootX + (secret.entrance === "left" ? 5 : -5),
      secret.y + secret.h - 28,
    );
    ctx.stroke();
    ctx.restore();
  }

  private drawSecretInterior(ctx: CanvasRenderingContext2D, secret: SecretArea): void {
    const earth = ctx.createLinearGradient(0, secret.y, 0, secret.y + secret.h);
    earth.addColorStop(0, "#252526");
    earth.addColorStop(1, "#3b302b");
    ctx.fillStyle = earth;
    ctx.fillRect(secret.x - 10, secret.y, secret.w + 20, secret.h);

    ctx.strokeStyle = "rgba(133,96,57,0.5)";
    ctx.lineWidth = 4;
    for (let x = secret.x + 55; x < secret.x + secret.w; x += 105) {
      ctx.beginPath();
      ctx.moveTo(x, secret.y);
      ctx.quadraticCurveTo(x - 18, secret.y + 34, x + 4, secret.y + 60);
      ctx.stroke();
    }

    ctx.fillStyle = "rgba(255,211,83,0.16)";
    for (let x = secret.x + 82; x < secret.x + secret.w; x += 130) {
      ctx.beginPath();
      ctx.arc(x, secret.y + secret.h - 20, 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawSlope(ctx: CanvasRenderingContext2D, slope: Slope): void {
    ctx.fillStyle = this.level.theme.ground;
    ctx.beginPath();
    if (slope.direction === "up") {
      ctx.moveTo(slope.x, slope.y + slope.h);
      ctx.lineTo(slope.x + slope.w, slope.y);
      ctx.lineTo(slope.x + slope.w, slope.y + slope.h);
    } else {
      ctx.moveTo(slope.x, slope.y);
      ctx.lineTo(slope.x + slope.w, slope.y + slope.h);
      ctx.lineTo(slope.x, slope.y + slope.h);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = this.level.theme.grass;
    ctx.lineWidth = 12;
    ctx.beginPath();
    if (slope.direction === "up") {
      ctx.moveTo(slope.x, slope.y + slope.h);
      ctx.lineTo(slope.x + slope.w, slope.y);
    } else {
      ctx.moveTo(slope.x, slope.y);
      ctx.lineTo(slope.x + slope.w, slope.y + slope.h);
    }
    ctx.stroke();
  }

  private drawSpring(ctx: CanvasRenderingContext2D, spring: Spring): void {
    const { x, y } = spring;
    const w = spring.w ?? 54;
    const squash = 1 + Math.sin(this.visualTime * 9 + x) * 0.04;
    ctx.save();
    ctx.translate(x + w / 2, y + 28);
    ctx.scale(1, squash);
    ctx.fillStyle = spring.kind === "fizz" ? "#48c9dc" : "#ec6249";
    ctx.fillRect(-w / 2, -13, w, 13);
    ctx.fillStyle = spring.kind === "fizz" ? "#e8ffff" : "#ffe25b";
    ctx.fillRect(-w / 2 + 5, -22, w - 10, 10);
    ctx.strokeStyle = spring.kind === "fizz" ? "#27697d" : "#573b35";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(-w / 3, 0);
    ctx.lineTo(w / 3, -13);
    ctx.moveTo(w / 3, 0);
    ctx.lineTo(-w / 3, -13);
    ctx.stroke();
    if (spring.kind === "fizz") {
      ctx.strokeStyle = "rgba(235,255,255,0.8)";
      ctx.lineWidth = 3;
      for (let bubble = 0; bubble < 3; bubble += 1) {
        const rise = (this.visualTime * 36 + bubble * 18 + x) % 48;
        ctx.beginPath();
        ctx.arc(-12 + bubble * 12, -28 - rise, 4 + bubble, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  private drawHazard(ctx: CanvasRenderingContext2D, hazard: Hazard): void {
    const { x, y, w, h } = hazard;
    const baseY = y + h;
    const spikeHeight = Math.max(46, h + 16);
    const spikeWidth = 22;

    ctx.save();
    if (hazard.kind === "shells") {
      ctx.shadowColor = "rgba(244,109,84,0.42)";
      ctx.shadowBlur = 10 + Math.sin(this.visualTime * 5 + x) * 2;
      ctx.fillStyle = "#5f493e";
      ctx.fillRect(x - 4, baseY - 9, w + 8, 11);
      let shellIndex = 0;
      for (let px = x; px < x + w; px += 24) {
        const center = Math.min(px + 12, x + w - 8);
        ctx.fillStyle = shellIndex % 2 === 0 ? "#ff7967" : "#ffd064";
        ctx.beginPath();
        ctx.moveTo(center - 12, baseY);
        ctx.quadraticCurveTo(center, baseY - spikeHeight - 8, center + 12, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = "#6d3f3f";
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.strokeStyle = "rgba(255,255,210,0.84)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(center, baseY - spikeHeight + 2);
        ctx.lineTo(center, baseY - 7);
        ctx.stroke();
        shellIndex += 1;
      }
      ctx.restore();
      return;
    }

    ctx.shadowColor = "rgba(255,92,52,0.45)";
    ctx.shadowBlur = 10 + Math.sin(this.visualTime * 5 + x) * 2;
    ctx.fillStyle = "#332f35";
    ctx.fillRect(x - 4, baseY - 9, w + 8, 11);

    let index = 0;
    for (let px = x; px < x + w; px += spikeWidth) {
      const right = Math.min(px + spikeWidth, x + w);
      const tipX = (px + right) / 2;
      const tipY = baseY - spikeHeight - (index % 2 === 0 ? 5 : 0);
      ctx.beginPath();
      ctx.moveTo(px, baseY);
      ctx.lineTo(tipX, tipY);
      ctx.lineTo(right, baseY);
      ctx.closePath();
      ctx.fillStyle = index % 2 === 0 ? "#ff5a45" : "#ff9a32";
      ctx.fill();
      ctx.strokeStyle = "#542e2d";
      ctx.lineWidth = 4;
      ctx.stroke();

      ctx.strokeStyle = "rgba(255,244,171,0.88)";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(tipX - 2, tipY + 9);
      ctx.lineTo(px + spikeWidth * 0.34, baseY - 10);
      ctx.stroke();
      index += 1;
    }
    ctx.shadowBlur = 0;
    ctx.fillStyle = "#ffd34d";
    for (let stripeX = x + 4; stripeX < x + w - 4; stripeX += 20) {
      ctx.fillRect(stripeX, baseY - 7, 10, 5);
    }
    ctx.restore();
  }

  private drawCollectible(ctx: CanvasRenderingContext2D, item: Collectible): void {
    const bob = Math.sin(this.visualTime * 4 + item.x * 0.03) * 5;
    ctx.save();
    ctx.translate(item.x, item.y + bob);

    if (item.kind === "fruit") {
      ctx.fillStyle = "#f7932d";
      ctx.beginPath();
      ctx.arc(0, 0, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffcf4c";
      ctx.beginPath();
      ctx.arc(-3, -4, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "#276f3e";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(1, -11);
      ctx.quadraticCurveTo(5, -20, 13, -16);
      ctx.stroke();
    } else if (item.kind === "golden-pin") {
      ctx.rotate(Math.sin(this.visualTime * 3 + item.x) * 0.12);
      ctx.shadowColor = "#ffe05a";
      ctx.shadowBlur = 18;
      ctx.fillStyle = "#ffe45d";
      ctx.beginPath();
      ctx.arc(0, -7, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(-6, 4);
      ctx.lineTo(0, 25);
      ctx.lineTo(7, 4);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#fff8bd";
      ctx.beginPath();
      ctx.arc(-5, -12, 5, 0, Math.PI * 2);
      ctx.fill();
    } else if (item.kind === "crown-piece") {
      ctx.shadowColor = "#fff39a";
      ctx.shadowBlur = 24;
      ctx.fillStyle = "#ffdc3f";
      ctx.beginPath();
      ctx.moveTo(-22, 11);
      ctx.lineTo(-18, -13);
      ctx.lineTo(-7, -2);
      ctx.lineTo(0, -21);
      ctx.lineTo(8, -2);
      ctx.lineTo(20, -14);
      ctx.lineTo(22, 11);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#fff4ad";
      ctx.fillRect(-18, 3, 36, 6);
    } else if (item.kind === "fizz-bubble") {
      ctx.shadowColor = "#6be8ff";
      ctx.shadowBlur = 22;
      ctx.fillStyle = "#45c9ed";
      ctx.beginPath();
      ctx.arc(-8, 2, 13, 0, Math.PI * 2);
      ctx.arc(8, 1, 13, 0, Math.PI * 2);
      ctx.arc(0, -10, 13, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "#e9fcff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(-3, -13, 5, Math.PI, Math.PI * 1.8);
      ctx.stroke();
      ctx.fillStyle = "#319256";
      ctx.fillRect(-3, -27, 6, 9);
    } else if (item.kind === "golden-glow") {
      ctx.rotate(this.reducedMotion ? 0 : this.visualTime * 1.5);
      ctx.shadowColor = "#ffe05c";
      ctx.shadowBlur = 26;
      ctx.fillStyle = "#ffe05c";
      ctx.beginPath();
      for (let point = 0; point < 10; point += 1) {
        const radius = point % 2 === 0 ? 25 : 11;
        const angle = -Math.PI / 2 + point * Math.PI / 5;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        if (point === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#fff9bd";
      ctx.beginPath();
      ctx.arc(-5, -5, 6, 0, Math.PI * 2);
      ctx.fill();
    } else if (item.kind === "pin-shield") {
      ctx.shadowColor = "#78a4ff";
      ctx.shadowBlur = 22;
      ctx.fillStyle = "#4d76d9";
      ctx.beginPath();
      ctx.moveTo(0, -27);
      ctx.lineTo(23, -15);
      ctx.lineTo(18, 15);
      ctx.quadraticCurveTo(0, 31, -18, 15);
      ctx.lineTo(-23, -15);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#ffe05c";
      ctx.beginPath();
      ctx.arc(0, -6, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(-3, 0, 6, 17);
    } else {
      ctx.shadowColor = "#ff9f53";
      ctx.shadowBlur = 20;
      ctx.fillStyle = "#f48a24";
      ctx.beginPath();
      ctx.ellipse(0, 3, 17, 22, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "#ffd052";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(-12, -5);
      ctx.lineTo(12, 10);
      ctx.moveTo(12, -5);
      ctx.lineTo(-12, 10);
      ctx.stroke();
      ctx.fillStyle = "#4aa652";
      ctx.beginPath();
      ctx.moveTo(-9, -17);
      ctx.lineTo(-3, -31);
      ctx.lineTo(1, -17);
      ctx.lineTo(9, -29);
      ctx.lineTo(8, -15);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawCritter(ctx: CanvasRenderingContext2D, critter: GroveCritter): void {
    const walking = critter.stunned <= 0 && !critter.defeated;
    const walkFrame = walking && !this.reducedMotion
      ? Math.floor(this.visualTime * 8 + critter.x * 0.035) % 2
      : 0;
    const bob = walkFrame === 1 ? -3 : 0;
    const state: CritterSpriteState = critter.defeated
      ? "defeated"
      : critter.stunned > 0
        ? "stunned"
        : walkFrame === 1
          ? "walk-b"
          : "walk-a";
    ctx.save();
    ctx.translate(Math.round(critter.x), Math.round(critter.y + bob));
    if (critter.direction < 0) ctx.scale(-1, 1);

    if (critter.defeated) {
      const progress = 1 - (critter.defeatTimer ?? 0) / CRITTER_DEFEAT_TIME;
      ctx.globalAlpha = 1 - progress;
      ctx.translate(progress * 24, -Math.sin(progress * Math.PI) * 70);
      ctx.rotate(progress * Math.PI * 1.7);
    }
    ctx.save();
    ctx.scale(3, 3);
    drawPixelCritter(ctx, critter, state);
    ctx.restore();
    if (state === "stunned") drawPixelStunStars(ctx, this.visualTime, this.reducedMotion);
    ctx.restore();
  }

  private drawCheckpoint(ctx: CanvasRenderingContext2D): void {
    const x = this.level.checkpointX;
    const groundY = this.level.groundY;
    ctx.fillStyle = "#70432d";
    ctx.fillRect(x, groundY - 145, 9, 145);
    ctx.fillStyle = this.checkpointReached ? "#ffd84c" : "#fff4bf";
    ctx.beginPath();
    ctx.moveTo(x + 8, groundY - 141);
    ctx.quadraticCurveTo(x + 67, groundY - 122, x + 11, groundY - 92);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#e16d2e";
    ctx.font = "900 22px Trebuchet MS";
    ctx.fillText("UP", x + 17, groundY - 112);
  }

  private drawGoal(ctx: CanvasRenderingContext2D): void {
    const x = this.level.goalX;
    const groundY = this.level.groundY;
    ctx.strokeStyle = "#8d4d29";
    ctx.lineWidth = 18;
    ctx.beginPath();
    ctx.moveTo(x, groundY);
    ctx.lineTo(x, groundY - 170);
    ctx.quadraticCurveTo(x + 42, groundY - 225, x + 84, groundY - 170);
    ctx.lineTo(x + 84, groundY);
    ctx.stroke();
    ctx.strokeStyle = "#4d9e48";
    ctx.lineWidth = 10;
    ctx.setLineDash([16, 8]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#ffda4e";
    ctx.beginPath();
    ctx.arc(x + 42, groundY - 205, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff8bd";
    ctx.beginPath();
    ctx.arc(x + 35, groundY - 212, 7, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawCart(ctx: CanvasRenderingContext2D, x: number): void {
    const y = this.level.groundY - 44;
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.sin(this.visualTime * 11) * 0.018);
    if (this.level.cart?.kind === "soda-float") {
      ctx.fillStyle = "rgba(110,226,245,0.3)";
      ctx.strokeStyle = "rgba(238,255,255,0.92)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.ellipse(0, -18, 52, 30, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#ef6258";
      ctx.beginPath();
      ctx.roundRect(-28, -64, 56, 54, 12);
      ctx.fill();
      ctx.fillStyle = "#ffe16a";
      ctx.fillRect(-23, -48, 46, 17);
      ctx.fillStyle = "#e9fbff";
      ctx.beginPath();
      ctx.arc(0, -73, 13, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.72)";
      ctx.lineWidth = 3;
      for (let bubble = 0; bubble < 3; bubble += 1) {
        const rise = (this.visualTime * 32 + bubble * 18) % 48;
        ctx.beginPath();
        ctx.arc(-18 + bubble * 18, -78 - rise, 5 + bubble, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = "#35556c";
      for (const wheelX of [-28, 28]) {
        ctx.beginPath();
        ctx.arc(wheelX, 4, 12, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      return;
    }
    ctx.fillStyle = "#a65e31";
    ctx.beginPath();
    ctx.roundRect(-45, -42, 90, 43, 8);
    ctx.fill();
    ctx.fillStyle = "#f3b13b";
    ctx.beginPath();
    ctx.arc(-25, -45, 15, 0, Math.PI * 2);
    ctx.arc(0, -50, 17, 0, Math.PI * 2);
    ctx.arc(25, -44, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#593927";
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(42, -31);
    ctx.lineTo(66, -56);
    ctx.lineTo(82, -56);
    ctx.stroke();
    ctx.fillStyle = "#3d4141";
    for (const wheelX of [-28, 28]) {
      ctx.beginPath();
      ctx.arc(wheelX, 5, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#bdc5b8";
      ctx.beginPath();
      ctx.arc(wheelX, 5, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#3d4141";
    }
    ctx.restore();
  }

  private drawParticle(ctx: CanvasRenderingContext2D, particle: Particle): void {
    ctx.globalAlpha = clamp(particle.life / particle.maxLife, 0, 1);
    ctx.fillStyle = particle.color;
    ctx.save();
    ctx.translate(particle.x, particle.y);
    ctx.rotate(particle.life * 9);
    ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  private drawPopupText(ctx: CanvasRenderingContext2D, popup: PopupText): void {
    ctx.save();
    ctx.globalAlpha = clamp(popup.life / popup.maxLife, 0, 1);
    ctx.font = "1000 18px Trebuchet MS";
    ctx.textAlign = "center";
    ctx.lineWidth = 5;
    ctx.strokeStyle = "rgba(24,51,60,0.88)";
    ctx.strokeText(popup.text, popup.x, popup.y);
    ctx.fillStyle = popup.color;
    ctx.fillText(popup.text, popup.x, popup.y);
    ctx.restore();
  }

  private drawPlayer(ctx: CanvasRenderingContext2D, playerX: number, playerY: number): void {
    const player = this.player;
    const centerX = playerX + player.w / 2;
    const feetY = playerY + player.h;
    const frame = this.playerFrame();
    const spriteFrame = SPRITE_FRAMES[frame] ?? SPRITE_FRAMES[0];

    ctx.save();
    if (player.invincible > 0 && Math.floor(player.invincible * 14) % 2 === 0) ctx.globalAlpha = 0.38;
    ctx.translate(centerX, feetY);
    if (player.gliding) ctx.rotate(player.facing * 0.08);
    if (player.facing < 0) ctx.scale(-1, 1);

    if (player.powerFlash > 0) {
      const progress = 1 - player.powerFlash / 0.62;
      ctx.save();
      ctx.scale(player.facing, 1);
      ctx.globalAlpha = 1 - progress;
      ctx.strokeStyle =
        player.power === "bubble"
          ? "#7feaff"
          : player.power === "glow"
            ? "#ffe15a"
            : player.power === "shield"
              ? "#78a4ff"
              : "#ffad5e";
      ctx.lineWidth = 7 - progress * 4;
      ctx.beginPath();
      ctx.arc(0, -52, 38 + progress * 42, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    if (player.power === "bubble") {
      ctx.save();
      ctx.scale(player.facing, 1);
      ctx.strokeStyle = "rgba(222,252,255,0.9)";
      ctx.fillStyle = "rgba(97,220,245,0.15)";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(0, -55, 56, 62, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.beginPath();
      ctx.ellipse(-20, -85, 10, 18, -0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (player.power === "glow") {
      ctx.save();
      ctx.scale(player.facing, 1);
      ctx.fillStyle = "rgba(255,224,78,0.42)";
      for (let ray = 0; ray < 10; ray += 1) {
        const angle = ray * Math.PI / 5 + (this.reducedMotion ? 0 : this.visualTime * 1.4);
        ctx.beginPath();
        ctx.ellipse(Math.cos(angle) * 46, -54 + Math.sin(angle) * 52, 6, 18, angle, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    } else if (player.power === "shield") {
      ctx.save();
      ctx.scale(player.facing, 1);
      ctx.strokeStyle = "rgba(120,164,255,0.92)";
      ctx.fillStyle = "rgba(82,120,218,0.12)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(0, -118);
      ctx.quadraticCurveTo(66, -102, 56, -45);
      ctx.quadraticCurveTo(0, 18, -56, -45);
      ctx.quadraticCurveTo(-66, -102, 0, -118);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    if (this.spriteReady) {
      const squash = player.landingTimer > 0 ? 0.91 : 1;
      const powerScale = player.power === "mini" ? 0.69 : 1;
      ctx.scale((1 / squash) * powerScale, squash * powerScale);
      const consistentFullHeight = frame <= 5 || (frame >= 8 && frame <= 15) || frame >= 28;
      const poseScale = consistentFullHeight ? GROUNDED_SPRITE_HEIGHT / spriteFrame.h : SPRITE_SCALE;
      const drawWidth = spriteFrame.w * poseScale;
      const drawHeight = spriteFrame.h * poseScale;
      if (this.goldenHeroUnlocked() || player.power === "glow") {
        ctx.filter = "sepia(1) saturate(4.8) hue-rotate(350deg) brightness(1.16)";
      }
      ctx.drawImage(
        this.sprite,
        spriteFrame.x,
        spriteFrame.y,
        spriteFrame.w,
        spriteFrame.h,
        -drawWidth / 2,
        -drawHeight,
        drawWidth,
        drawHeight,
      );
    } else {
      ctx.fillStyle = this.goldenHeroUnlocked() ? "#ffe05c" : "#f39a2d";
      ctx.beginPath();
      ctx.arc(0, -45, 38, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private playerFrame(): number {
    const player = this.player;
    if (this.state === "won" || this.state === "worldcomplete") return 29;
    if (player.respawnTimer > 0) return 30;
    if (player.powerFlash > 0.28) return 28;
    if (!player.onGround) {
      if (player.gliding) return 22;
      if (player.vy < -560) return 17;
      if (player.vy < -130) return 18;
      if (player.vy < 180) return 19;
      if (player.vy < 560) return 20;
      return 21;
    }
    if (player.landingTimer > 0) return 16;
    const inputDirection = Number(this.input.right) - Number(this.input.left);
    if (inputDirection !== 0 && Math.abs(player.vx) > 190 && Math.sign(player.vx) !== inputDirection) return 24;
    if (Math.abs(player.vx) > 35) return 8 + Math.floor(player.runCycle) % 8;
    const idleCycle = this.visualTime % 7.2;
    if (idleCycle > 6.85) return 2;
    if (idleCycle > 5.65 && idleCycle < 6.2) return 4;
    return Math.floor(this.visualTime * 1.35) % 2;
  }

  private drawLevelIntro(ctx: CanvasRenderingContext2D): void {
    if (this.levelIntroTimer <= 0 || this.state !== "playing") return;
    const fadeIn = clamp((2.6 - this.levelIntroTimer) / 0.28, 0, 1);
    const fadeOut = clamp(this.levelIntroTimer / 0.45, 0, 1);
    const alpha = this.reducedMotion ? fadeOut : Math.min(fadeIn, fadeOut);
    const compact = this.viewWidth < 720;
    const width = Math.min(this.viewWidth - 36, compact ? 420 : 520);
    const x = this.viewWidth / 2 - width / 2;
    const y = compact ? 150 : 122;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(24,51,60,0.88)";
    ctx.beginPath();
    ctx.roundRect(x, y, width, compact ? 92 : 104, 22);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,231,126,0.9)";
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = "#ffd84c";
    ctx.textAlign = "center";
    ctx.font = `1000 ${compact ? 14 : 16}px Trebuchet MS`;
    ctx.fillText(
      this.level.bonus
        ? `CROWN VAULT · BONUS CHALLENGE · ${this.level.subtitle.toUpperCase()}`
        : `WORLD ${this.level.worldNumber} · LEVEL ${this.level.worldLevel} · ${this.level.subtitle.toUpperCase()}`,
      this.viewWidth / 2,
      y + 28,
    );
    ctx.fillStyle = "#fff7d4";
    ctx.font = `1000 ${compact ? 25 : 31}px Trebuchet MS`;
    ctx.fillText(this.level.name, this.viewWidth / 2, y + 59);
    ctx.font = `800 ${compact ? 12 : 14}px Trebuchet MS`;
    ctx.fillStyle = "rgba(255,247,212,0.82)";
    ctx.fillText(this.level.objective, this.viewWidth / 2, y + (compact ? 81 : 87));
    ctx.restore();
  }

  private drawDebugOverlay(ctx: CanvasRenderingContext2D, playerX: number, playerY: number): void {
    ctx.save();
    ctx.lineWidth = 2;
    ctx.font = "800 12px monospace";
    ctx.strokeStyle = "#49f4ff";
    ctx.strokeRect(playerX, playerY, this.player.w, this.player.h);

    ctx.strokeStyle = "rgba(87,255,129,0.9)";
    for (const platform of this.runtimePlatforms) {
      if (platform.active) ctx.strokeRect(platform.x, platform.y, platform.w, platform.h);
    }

    ctx.strokeStyle = "rgba(255,76,76,0.95)";
    for (const hazard of this.level.hazards) ctx.strokeRect(hazard.x, hazard.y, hazard.w, hazard.h);
    ctx.strokeStyle = "rgba(255,220,85,0.92)";
    for (const gate of this.level.powerGates ?? []) ctx.strokeRect(gate.x, gate.y, gate.w, gate.h);

    ctx.strokeStyle = "rgba(255,218,79,0.92)";
    for (const item of this.items) {
      if (item.collected) continue;
      const radius = item.kind === "golden-pin" || item.kind === "crown-piece" ? 24 : 14;
      ctx.beginPath();
      ctx.arc(item.x, item.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    for (const critter of this.critters) {
      if (critter.defeated) continue;
      ctx.strokeStyle = "rgba(255,116,212,0.92)";
      ctx.strokeRect(critter.x - CRITTER_HALF_WIDTH, critter.y - CRITTER_HEIGHT, CRITTER_HALF_WIDTH * 2, CRITTER_HEIGHT + 3);
      ctx.strokeStyle = "rgba(255,116,212,0.4)";
      ctx.strokeRect(critter.minX - CRITTER_HALF_WIDTH, critter.y - CRITTER_HEIGHT, critter.maxX - critter.minX + CRITTER_HALF_WIDTH * 2, CRITTER_HEIGHT + 3);
    }
    ctx.restore();
  }

  private drawHud(ctx: CanvasRenderingContext2D): void {
    const compact = this.viewWidth < 680;
    const tiny = this.viewWidth < 520;
    const scale = tiny ? 0.72 : compact ? 0.76 : 1;
    ctx.save();
    ctx.scale(scale, scale);
    const safeWidth = this.viewWidth / scale;

    this.drawHudPill(ctx, tiny ? 12 : 18, 18, compact ? 150 : 205, 54);
    ctx.fillStyle = "#18333c";
    ctx.font = `1000 ${compact ? 18 : 21}px Trebuchet MS`;
    ctx.fillText(`SCORE ${String(this.score).padStart(5, "0")}`, tiny ? 27 : 34, 52);

    const pinWidth = compact ? 122 : 176;
    const pinX = tiny ? 12 : safeWidth / 2 - pinWidth / 2;
    const secondRowY = tiny ? 80 : 18;
    this.drawHudPill(ctx, pinX, secondRowY, pinWidth, 54);
    ctx.fillStyle = "#e8a21c";
    ctx.beginPath();
    ctx.arc(pinX + 27, secondRowY + 25, 11, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#18333c";
    ctx.font = `1000 ${compact ? 17 : 19}px Trebuchet MS`;
    ctx.fillText(`${this.pins}/3  ${this.crownFound ? "♛" : "♙"}`, pinX + 47, secondRowY + 34);

    const heartsX = safeWidth - (tiny ? 137 : compact ? 380 : 410);
    const heartsY = tiny ? 80 : 18;
    this.drawHudPill(ctx, heartsX, heartsY, compact ? 125 : 172, 54);
    for (let index = 0; index < 3; index += 1) {
      this.drawHeart(ctx, heartsX + 30 + index * (compact ? 34 : 43), heartsY + 26, index < this.lives);
    }

    if (this.player.power || this.goldenHeroUnlocked()) {
      const powerY = tiny ? 142 : 82;
      const timed = this.player.powerTimer > 0 ? ` ${Math.ceil(this.player.powerTimer)}s` : "";
      const label = this.player.power === "bubble"
        ? `● FIZZ${timed}`
        : this.player.power === "glow"
          ? `✦ GLOW${timed}`
          : this.player.power === "shield"
            ? "⬟ SHIELD"
            : this.player.power === "mini"
              ? "◆ MINI"
              : "♛ GOLD HERO";
      const color = this.player.power === "bubble"
        ? "#278eac"
        : this.player.power === "glow" || !this.player.power
          ? "#a96f00"
          : this.player.power === "shield"
            ? "#416bc9"
            : "#d45c25";
      ctx.fillStyle = "rgba(255,247,212,0.9)";
      ctx.beginPath();
      ctx.roundRect(tiny ? 12 : 18, powerY, compact ? 126 : 154, 34, 14);
      ctx.fill();
      ctx.fillStyle = color;
      ctx.font = `900 ${compact ? 13 : 15}px Trebuchet MS`;
      ctx.fillText(label, tiny ? 25 : 31, powerY + 23);
    }

    const levelLabel = this.level.bonus
      ? `BONUS · ${this.level.name.toUpperCase()}`
      : `W${this.level.worldNumber}-${this.level.worldLevel} · ${this.level.name.toUpperCase()}`;
    ctx.font = `900 ${compact ? 13 : 15}px Trebuchet MS`;
    const labelWidth = Math.min(290, ctx.measureText(levelLabel).width + 34);
    const labelX = safeWidth / 2 - labelWidth / 2;
    ctx.fillStyle = "rgba(24,51,60,0.78)";
    ctx.beginPath();
    ctx.roundRect(labelX, this.level.worldHeight / scale - 48, labelWidth, 32, 14);
    ctx.fill();
    ctx.fillStyle = "#fff7d4";
    ctx.textAlign = "center";
    ctx.fillText(levelLabel, safeWidth / 2, this.level.worldHeight / scale - 27);
    ctx.textAlign = "start";

    if (!tiny && this.debugMode) {
      ctx.fillStyle = "rgba(24,51,60,0.66)";
      ctx.beginPath();
      ctx.roundRect(18, this.level.worldHeight / scale - 47, 232, 30, 13);
      ctx.fill();
      ctx.fillStyle = "#fff7d4";
      ctx.font = "900 12px Trebuchet MS";
      ctx.fillText(`DEV  2 ◀ LEVEL ▶ 1  ·  F7 ${this.showDebugHitboxes ? "HIDE" : "HITBOXES"}`, 30, this.level.worldHeight / scale - 27);
    }

    if (this.level.cart && this.state === "playing") {
      const remaining = Math.max(0, Math.round((this.cartX - this.player.x - this.player.w) / 10) * 10);
      const cartPillWidth = this.level.cart.kind === "soda-float"
        ? this.cartCaught ? 318 : 184
        : this.cartCaught ? 230 : 170;
      ctx.fillStyle = "rgba(255,247,212,0.9)";
      ctx.beginPath();
      ctx.roundRect(safeWidth / 2 - cartPillWidth / 2, tiny ? 142 : 82, cartPillWidth, 34, 14);
      ctx.fill();
      ctx.fillStyle = "#8b4c2c";
      ctx.font = `900 ${compact ? 13 : 15}px Trebuchet MS`;
      ctx.textAlign = "center";
      ctx.fillText(
        this.level.cart.kind === "soda-float"
          ? this.cartCaught
            ? "FLOAT CAUGHT • GUIDE TO THE LIGHTHOUSE"
            : remaining > 100
              ? `FLOAT ${remaining}m AHEAD`
              : "FLOAT IN REACH!"
          : this.cartCaught
            ? "CART CAUGHT • PUSH TO THE GATE"
            : remaining > 100
              ? `CART ${remaining}m AHEAD`
              : "CART IN REACH!",
        safeWidth / 2,
        (tiny ? 142 : 82) + 23,
      );
      ctx.textAlign = "start";
    }
    ctx.restore();
  }

  private drawHudPill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    ctx.fillStyle = "rgba(255,247,212,0.9)";
    ctx.strokeStyle = "rgba(24,51,60,0.72)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 17);
    ctx.fill();
    ctx.stroke();
  }

  private drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, active: boolean): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(0.78, 0.78);
    ctx.fillStyle = active ? "#ee5d45" : "#bea99b";
    ctx.beginPath();
    ctx.moveTo(0, 15);
    ctx.bezierCurveTo(-25, 0, -19, -17, -8, -17);
    ctx.bezierCurveTo(-1, -17, 0, -10, 0, -6);
    ctx.bezierCurveTo(0, -10, 1, -17, 8, -17);
    ctx.bezierCurveTo(19, -17, 25, 0, 0, 15);
    ctx.fill();
    ctx.restore();
  }

  private stats(): GameStats {
    const currentRecord = this.progress.levels[this.currentLevelIndex] ?? emptySavedLevel();
    const crownCount = this.campaignCrownCount();
    return {
      score: this.score,
      pins: this.pins,
      fruits: this.fruits,
      lives: this.lives,
      time: this.elapsed,
      bestTime: currentRecord.bestTime,
      checkpoint: this.checkpointReached,
      crown: this.crownFound,
      levelIndex: this.currentLevelIndex,
      levelName: this.level.name,
      levelSubtitle: this.level.subtitle,
      levelObjective: this.level.objective,
      worldNumber: this.level.worldNumber,
      worldName: this.level.worldName,
      worldLevel: this.level.worldLevel,
      worldLevelCount: this.level.bonus ? 1 : LEVELS.filter((level) => level.worldNumber === this.level.worldNumber).length,
      totalLevels: LEVELS.length,
      parTime: this.level.parTime,
      bonus: Boolean(this.level.bonus),
      canAdvance: !this.level.bonus && this.currentLevelIndex + 1 < LEVELS.length,
      crownCount,
      bonusUnlocked: crownCount >= 5,
      goldenHero: crownCount === LEVELS.length,
      levels: ALL_LEVELS.map((level, index) => {
        const record = this.progress.levels[index] ?? emptySavedLevel();
        return {
          name: level.name,
          worldNumber: level.worldNumber,
          worldName: level.worldName,
          worldLevel: level.worldLevel,
          bonus: Boolean(level.bonus),
          unlocked: this.isUnlocked(index),
          completed: record.completed,
          bestPins: record.bestPins,
          crown: record.crown,
          bestTime: record.bestTime,
          grade: record.grade,
        };
      }),
    };
  }
}
